/**
 * ProjectManagerDashboardShell — F5: Project Manager Persona Dashboard Layout
 *
 * Renders the Project Manager persona dashboard. Each widget slot is driven by
 * the ordered visible widget list from DashboardWidgetPreferencesContext so that
 * personalisation (visibility and ordering) applies automatically.
 *
 * Individual widget implementations are added in tasks F21–F28. Until each widget
 * is built, a clearly labelled "Coming Soon" placeholder card is rendered so the
 * shell is fully functional as a layout container from day one.
 *
 * PRD Layout (§4):
 *   Row 1  — My Project Pulse (4 KPI tiles, full width)
 *   Row 2  — My Projects Health (full width — centrepiece)
 *   Row 3  — Budget & Hours Burn | Schedule Health
 *   Row 4  — My Team This Week | Upcoming Billing Events
 *   Row 5  — Timesheet Status (full width)
 *   Row 6  — Quick Links (full width)
 *
 * Scope: all PM widgets are filtered to `projectManagerId = currentUser.id`
 * (enforced at the API layer — the shell renders whatever the API returns).
 */

import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Construction,
  ArrowRight,
  FolderKanban,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Pencil,
  CalendarClock,
  TrendingDown,
  TrendingUp,
  Banknote,
  FileX,
  BarChart3,
  Diamond,
  DollarSign,
  CalendarDays,
  WifiOff,
  Activity,
  ShieldAlert,
  Layers,
  Users,
  UserCheck,
  UserX,
  UserMinus,
  ChevronsDown,
  ChevronsUp,
  Percent,
  Calendar,
  RefreshCw,
  ClipboardList,
  FileText,
  BookOpen,
  ChevronRight,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatHours } from '@/lib/budget-utils';
import { useDashboardData } from '@/contexts/DashboardDataContext';
import { useDashboardWidgetPreferences } from '@/contexts/DashboardWidgetPreferencesContext';
import {
  isPulseTileHiddenForRelease,
  isWidgetHiddenForReleaseAnyPersona,
} from '@/config/dashboardReleaseConfig';
import { useAuth } from '@/providers/AuthProvider';
import { KpiCard, CompactKpiCard } from '@/components/kpi-card';
import { PROJECT_ROLE_LABELS } from '@/types';
import type { ResourceAllocation, Employee, Project, WIPReport } from '@/types';
import type { BillingMilestone } from '@/types/budget.types';
import { getBudgetDataByProjectId } from '@/lib/mock-data/budget-mock-data';
import {
  fetchProjects,
  fetchResourceAllocations,
  fetchEmployees,
  fetchDashboardKpiSnapshot,

  fetchDashboardScheduleHealth,
  fetchDashboardBillingEvents,
  fetchPMDashboardProjectsSummary,
  getDataMode,
} from '@/lib/data-service';
import type { DashboardKpiSnapshot, TimeEntryWithEmployee, ScheduleHealthResponse, DashboardBillingEvent, PMProjectSummaryRow } from '@/lib/data-service';
import {
  mockEmployees,
  getAllocationsByProject,
} from '@/lib/mock-data';
import { useCompany } from '@/contexts/CompanyContext';
import { useDashboardIntegrationStatus } from '@/hooks/use-dashboard-integration-status';
import { useDashboardWipView } from '@/hooks/use-dashboard-wip-view';
import { useWipThresholds } from '@/hooks/use-wip-thresholds';
import { useEacSettings } from '@/contexts/EacSettingsContext';

// ── Coming-soon placeholder ───────────────────────────────────────────────────

interface ComingSoonCardProps {
  widgetId: string;
  title: string;
  description: string;
  fullWidth?: boolean;
}

function ComingSoonCard({ widgetId, title, description, fullWidth }: ComingSoonCardProps) {
  return (
    <Card className={`border-dashed border-2 border-muted ${fullWidth ? 'col-span-full' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Construction className="h-4 w-4 shrink-0" />
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        </div>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground italic">
          Widget implementation coming soon (task {widgetId}).
        </p>
      </CardContent>
    </Card>
  );
}

/** Under-billing below this is noise and is not listed. */
const PM_UNDER_BILLING_THRESHOLD = 500;

// ── PM shell ──────────────────────────────────────────────────────────────────

interface ProjectManagerDashboardShellProps {
  onNavigate?: (page: string) => void;
  /**
   * DB-6: When true, Finance cross-persona widgets (Billing Pipeline, WIP Aging)
   * added by this PM will display a "Showing your projects only" scope badge.
   * The badge signals that data is scoped to the PM's accessible projects, not
   * company-wide (enforced at the API level — DB-BE-1).
   */
  showFinanceWidgetScopeIndicator?: boolean;
}

export function ProjectManagerDashboardShell({ onNavigate, showFinanceWidgetScopeIndicator = false }: ProjectManagerDashboardShellProps) {
  const navigateHook = useNavigate();
  const handleNavigate = onNavigate || ((page: string) => navigateHook(`/${page}`));

  const { activeProjects: ctxActiveProjects, wipReports, projectsLoading: ctxProjectsLoading, wipLoading } = useDashboardData();
  const { orderedVisibleWidgets } = useDashboardWidgetPreferences();
  const { authenticatedUser } = useAuth();
  const { plannerStatus } = useDashboardIntegrationStatus();
  const { thresholds: wipThresholds } = useWipThresholds();
  const isPlannerConnected = plannerStatus.isConnected;
  const { currentCompany } = useCompany();

  // ── Live data for team, milestones, and timesheets ───────────────────────────
  // In live mode, fetch employees and resource allocations from the backend API
  // instead of using hardcoded mock data. Falls back to mock data in demo/tour modes.
  const dataMode = getDataMode();
  const isLiveMode = dataMode === 'live';

  // In live mode, the dashboard context no longer fetches the full Project[] array.
  // Fetch only the PM's active projects via the lightweight SQL-aggregated endpoint.
  const [pmProjects, setPmProjects] = useState<Project[]>([]);
  const [pmProjectsLoading, setPmProjectsLoading] = useState(isLiveMode);

  const pmId = authenticatedUser?.id ? parseInt(authenticatedUser.id, 10) : undefined;

  useEffect(() => {
    if (!isLiveMode) return;
    let cancelled = false;
    setPmProjectsLoading(true);
    fetchPMDashboardProjectsSummary(pmId)
      .then(resp => {
        if (cancelled) return;
        const mapped: Project[] = resp.projects.map((r: PMProjectSummaryRow) => ({
          id: r.id,
          name: r.name,
          code: r.code,
          clientNames: r.clientNames,
          health: r.health,
          statusId: r.statusId,
          completionPercent: r.completionPercent,
          completionPercentOverride: r.completionPercentOverride,
          budgetRevenue: r.budgetRevenue,
          actualRevenue: r.actualRevenue,
          actualLabour: r.actualLabour,
          actualExpense: r.actualExpense,
          budgetLabour: r.budgetLabour,
          budgetExpense: r.budgetExpense,
          budgetCost: r.budgetCost,
          actualCosts: r.actualCosts,
          budgetHours: r.budgetHours,
          actualHours: r.actualHours,
          billableHours: r.billableHours,
          hoursVariance: r.hoursVariance,
          costVariancePercent: r.costVariancePercent,
          wipAmount: r.wipAmount,
          endDate: r.endDate,
          forecastEndDate: r.forecastEndDate,
          startDate: r.startDate,
          projectManagerId: r.projectManagerId,
          type: r.type,
        } as unknown as Project));
        setPmProjects(mapped);
      })
      .catch(() => { if (!cancelled) setPmProjects([]); })
      .finally(() => { if (!cancelled) setPmProjectsLoading(false); });
    return () => { cancelled = true; };
  }, [isLiveMode, pmId, currentCompany?.id]);

  // Unified activeProjects: live mode uses local fetch, demo/tour uses context
  const activeProjects = isLiveMode ? pmProjects : ctxActiveProjects;
  const projectsLoading = isLiveMode ? pmProjectsLoading : ctxProjectsLoading;

  const [liveEmployees, setLiveEmployees] = useState<Employee[]>([]);
  const [liveAllocations, setLiveAllocations] = useState<ResourceAllocation[]>([]);
  const [liveBillingEvents, setLiveBillingEvents] = useState<DashboardBillingEvent[]>([]);
  const [liveDataLoading, setLiveDataLoading] = useState(false);

  useEffect(() => {
    if (!isLiveMode || projectsLoading) return;

    let cancelled = false;
    setLiveDataLoading(true);

    const loadLiveData = async () => {
      const [empResult, allocResult] = await Promise.allSettled([
        fetchEmployees('dashboard'),
        fetchResourceAllocations('dashboard'),
      ]);

      if (cancelled) return;

      if (empResult.status === 'fulfilled') {
        setLiveEmployees(empResult.value as Employee[]);
      } else {
        console.error('[PMDashboard] Failed to fetch employees:', empResult.reason);
      }

      if (allocResult.status === 'fulfilled') {
        setLiveAllocations(allocResult.value as ResourceAllocation[]);
      } else {
        console.error('[PMDashboard] Failed to fetch allocations:', allocResult.reason);
      }

      setLiveDataLoading(false);
    };

    void loadLiveData();
    return () => { cancelled = true; };
  }, [isLiveMode, projectsLoading, currentCompany?.id]);

  // Fetch billing events aggregated at SQL level (replaces per-project milestone calls)
  useEffect(() => {
    if (!isLiveMode) return;
    let cancelled = false;
    const pmId = authenticatedUser?.id ? parseInt(authenticatedUser.id, 10) : undefined;
    void fetchDashboardBillingEvents(pmId).then(events => {
      if (!cancelled) setLiveBillingEvents(events);
    });
    return () => { cancelled = true; };
  }, [isLiveMode, authenticatedUser?.id, currentCompany?.id]);

  // ── KPI Snapshot for PM weekly deltas (live mode) ──────────────────────────
  const [pmPriorSnapshot, setPmPriorSnapshot] = useState<DashboardKpiSnapshot | null>(null);

  useEffect(() => {
    // The endpoint requires the X-Company-Id header, which authFetch only sends
    // once company context has resolved.
    if (!isLiveMode || !currentCompany?.id) return;
    let cancelled = false;
    const today = new Date().toISOString().slice(0, 10);
    void fetchDashboardKpiSnapshot('project_manager', 'weekly', today).then(snap => {
      if (!cancelled) setPmPriorSnapshot(snap);
    });
    return () => { cancelled = true; };
  }, [isLiveMode, currentCompany?.id]);

  // ── Helpers to abstract live vs mock data access ─────────────────────────────

  /** Get allocations for a project — uses live API data in live mode, mock in demo/tour */
  function getProjectAllocations(projectId: number): ResourceAllocation[] {
    if (isLiveMode) {
      return liveAllocations.filter(a => a.projectId === projectId);
    }
    return getAllocationsByProject(projectId);
  }

  /** Find an employee by ID — uses live API data in live mode, mock in demo/tour */
  function findEmployee(employeeId: number): Employee | undefined {
    if (isLiveMode) {
      return liveEmployees.find(e => e.id === employeeId);
    }
    return mockEmployees.find(e => e.id === employeeId);
  }

  /** Get billing milestones for a project — uses mock data in demo/tour */
  function getProjectMilestones(projectId: number): BillingMilestone[] {
    if (isLiveMode) {
      // In live mode, milestones are fetched via the aggregated billing-events endpoint.
      // This function is only used for demo/tour mode fallback.
      return [];
    }
    try {
      const budgetData = getBudgetDataByProjectId(projectId);
      return budgetData?.salesBudget?.milestones ?? [];
    } catch {
      return [];
    }
  }

  // ── F24: Schedule Health — single aggregated SQL call ────────────────────────
  const [scheduleHealthData, setScheduleHealthData] = useState<ScheduleHealthResponse | null>(null);
  const [schedulingLoading, setSchedulingLoading] = useState(false);

  // ── EAC settings (showHoursToComplete + eacMethod) ───────────────────────────
  const { eacMethod, showHoursToComplete, setShowHoursToComplete } = useEacSettings();

  // ── Widget-level state (hoisted to component body for stable hook ordering) ──
  const [burnTab, setBurnTab] = useState<'cost' | 'hours'>('cost');
  const [showFutureEvents, setShowFutureEvents] = useState(false);
  // DEV-only toggle: simulates whether the PM has submitted HTC data
  const [devHtcAvailable, setDevHtcAvailable] = useState(false);

  useEffect(() => {
    if (!isLiveMode) return;

    let cancelled = false;
    setSchedulingLoading(true);
    const pmId = authenticatedUser?.id ? parseInt(authenticatedUser.id, 10) : undefined;

    void fetchDashboardScheduleHealth(pmId).then(data => {
      if (!cancelled) {
        setScheduleHealthData(data);
        setSchedulingLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [isLiveMode, authenticatedUser?.id, currentCompany?.id]);

  // ── F21: PM-scoped derived data ──────────────────────────────────────────────
  // Parse authenticated user id to match projectManagerId (number).
  // Falls back to showing all active projects when there is no match (demo / seed data).
  const currentPmId = authenticatedUser?.id ? parseInt(authenticatedUser.id, 10) : null;

  const pmActiveProjects = useMemo(() => {
    if (!currentPmId || isNaN(currentPmId)) return activeProjects;
    const scoped = activeProjects.filter(p => p.projectManagerId === currentPmId);
    // Graceful fallback: if the current user has no matching projects (dev/demo), show all
    return scoped.length > 0 ? scoped : activeProjects;
  }, [activeProjects, currentPmId]);

  // WIP figures for the cross-persona finance widgets a PM can add. Live mode
  // reads them from the dashboard WIP summary endpoint, already scoped to this
  // PM server-side; demo/tour derives them from the mock rows the context holds,
  // scoped to the projects on screen.
  const pmWipFilter = useCallback(
    (row: WIPReport) => pmActiveProjects.some(p => p.id === row.projectId),
    [pmActiveProjects],
  );
  const wipView = useDashboardWipView({
    underBillingThreshold: PM_UNDER_BILLING_THRESHOLD,
    agedWipDaysThreshold: wipThresholds.agedWipDaysThreshold,
    filter: pmWipFilter,
  });

  // Next billing event per project. Live mode has the pre-aggregated billing
  // events endpoint; demo/tour falls back to the mock WIP rows.
  const nextBillingEventByProject = useMemo(() => {
    const map = new Map<number, string>();
    if (isLiveMode) {
      for (const evt of liveBillingEvents) {
        if (!evt.dueDate) continue;
        const existing = map.get(evt.projectId);
        if (!existing || evt.dueDate < existing) map.set(evt.projectId, evt.dueDate);
      }
    } else {
      for (const row of wipReports) {
        if (row.nextBillingEvent) map.set(row.projectId, row.nextBillingEvent);
      }
    }
    return map;
  }, [isLiveMode, liveBillingEvents, wipReports]);

  // Tile 1 — My Active Projects
  const myActiveCount = pmActiveProjects.length;

  // Tile 2 — Hours Remaining (positive = buffer, negative = overrun)
  const myHoursRemaining = useMemo(
    () => pmActiveProjects.reduce((sum, p) => sum + (p.hoursVariance ?? 0), 0),
    [pmActiveProjects],
  );

  // Tile 3 — Projects On Track (health === 'green')
  const myOnTrackCount = useMemo(
    () => pmActiveProjects.filter(p => p.health === 'green').length,
    [pmActiveProjects],
  );

  // Tile 4 — Pending Actions: overdue billing milestones on PM's active projects.
  // In live mode, reads from aggregated billing-events SQL endpoint.
  // In demo/tour, reads from budget mock layer.
  const myOverdueMilestoneCount = useMemo(() => {
    if (isLiveMode) {
      return liveBillingEvents.filter(e => e.eventType === 'overdue').length;
    }
    let count = 0;
    for (const project of pmActiveProjects) {
      const milestones = getProjectMilestones(project.id);
      count += milestones.filter(m => m.status === 'overdue').length;
    }
    return count;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmActiveProjects, liveBillingEvents, isLiveMode]);

  // Weekly deltas — in live mode, derive from KPI snapshot (DashboardKpiSnapshot).
  // Falls back to 0 delta if no snapshot exists yet.
  // In demo/tour, use seeded approximation.
  const pmIdSeed = pmActiveProjects.reduce((sum, p) => sum + p.id, 0);
  const priorActiveCount = useMemo(() => {
    if (isLiveMode) return pmPriorSnapshot?.pmActiveProjectCount ?? myActiveCount;
    return Math.max(0, myActiveCount + (pmIdSeed % 3) - 1);
  }, [isLiveMode, pmPriorSnapshot, myActiveCount, pmIdSeed]);
  const priorOnTrackCount = useMemo(() => {
    if (isLiveMode) return pmPriorSnapshot?.pmOnTrackCount ?? myOnTrackCount;
    return Math.max(0, myOnTrackCount + ((pmIdSeed % 5) - 2));
  }, [isLiveMode, pmPriorSnapshot, myOnTrackCount, pmIdSeed]);
  const priorOverdueCount = useMemo(() => {
    if (isLiveMode) return pmPriorSnapshot?.pmOverdueMilestoneCount ?? myOverdueMilestoneCount;
    return Math.max(0, myOverdueMilestoneCount - (pmIdSeed % 2));
  }, [isLiveMode, pmPriorSnapshot, myOverdueMilestoneCount, pmIdSeed]);
  const activeCountDelta   = myActiveCount   - priorActiveCount;
  const onTrackDelta       = myOnTrackCount  - priorOnTrackCount;
  const overdueDelta       = myOverdueMilestoneCount - priorOverdueCount;

  // Per-widget data dependency map — widgets render progressively as their
  // data arrives instead of blocking the entire dashboard on the slowest call.
  const PM_WIDGET_DEPS: Record<string, ('projects' | 'wip')[]> = {
    pm_pulse:                  ['projects'],
    my_projects_health:        ['projects'],
    budget_hours_burn:         ['projects'],
    schedule_health:           ['projects'],
    my_team_this_week:         ['projects'],
    upcoming_billing_events:   ['projects'],
    timesheet_status:          ['projects'],
    quick_links_pm:            [],
    billing_pipeline:          ['wip'],
    wip_aging_overview:        ['wip'],
    client_concentration_risk: ['projects'],
    portfolio_profitability:   ['projects'],
  };

  function isPmWidgetReady(widgetId: string): boolean {
    const deps = PM_WIDGET_DEPS[widgetId] ?? ['projects'];
    if (deps.includes('projects') && (projectsLoading || (isLiveMode && liveDataLoading))) return false;
    if (deps.includes('wip') && wipLoading) return false;
    return true;
  }

  function PmWidgetSkeleton({ widgetId }: { widgetId: string }) {
    if (widgetId === 'pm_pulse') {
      return (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardHeader className="pb-2"><Skeleton className="h-4 w-32" /></CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-3 w-28" />
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }
    return (
      <Card>
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent><Skeleton className="h-48 w-full" /></CardContent>
      </Card>
    );
  }

  // ── Widget renderer ──────────────────────────────────────────────────────────

  function renderWidget(widgetId: string) {
    switch (widgetId) {
      // ── My Project Pulse (KPI tiles) — F21 ─────────────────────────────────
      // PRD §4, Insight 1 — 4 KPI tiles scoped to current PM's projects.
      // Trend: weekly delta (prior-week approximation via seeded offset).
      case 'pm_pulse': {
        const allOnTrack = myOnTrackCount === myActiveCount && myActiveCount > 0;
        const hasOverdue = myOverdueMilestoneCount > 0;

        // ── Mock pm_pulse EAC data ────────────────────────────────────────────
        const MOCK_PM_PULSE = {
          myHoursToComplete: 280,   // Σ hoursToComplete when htcAvailable: true
          myPortfolioEac:  487500,  // Σ calculateEAC() across PM's active projects
          eacVsBudget:     'amber' as 'green' | 'amber' | 'red',
          totalBudget:     490000,
        };

        // ── Hours Remaining tile — HTC-aware logic ────────────────────────────
        // Off  → hoursVariance, no sub-label
        // On + htcAvailable → hoursToComplete, muted sub-label
        // On + !htcAvailable → hoursVariance, amber sub-label
        const htcMode = showHoursToComplete && devHtcAvailable;
        const htcOffNoData = showHoursToComplete && !devHtcAvailable;
        const displayHours = htcMode ? MOCK_PM_PULSE.myHoursToComplete : myHoursRemaining;
        const hoursIsNegative = displayHours < 0;

        // ── EAC tile accent colour ─────────────────────────────────────────────
        const eacAccentMap: Record<'green' | 'amber' | 'red', 'success' | 'warning' | 'error'> = {
          green: 'success',
          amber: 'warning',
          red:   'error',
        };
        const eacAccent = eacAccentMap[MOCK_PM_PULSE.eacVsBudget];

        // EAC method display label
        const eacMethodLabel: Record<string, string> = {
          simple:             'Simple',
          'performance-based': 'Performance',
          're-estimate':       'Re-estimate',
        };
        const eacMethodDisplay = eacMethodLabel[eacMethod] ?? eacMethod;

        return (
          <>
            {/* DEV-only toggles: showHoursToComplete comes from Settings; htcAvailable simulates PM HTC submission */}
            {import.meta.env.DEV && (
              <div className="flex flex-wrap items-center gap-4 rounded-md border border-dashed border-amber-400 bg-amber-50 px-3 py-2 text-xs dark:bg-amber-950/20 mb-1">
                <span className="font-semibold text-amber-700 dark:text-amber-400">DEV</span>
                <label className="flex items-center gap-1.5 cursor-pointer text-amber-800 dark:text-amber-300">
                  <input
                    type="checkbox"
                    checked={showHoursToComplete}
                    onChange={e => setShowHoursToComplete(e.target.checked)}
                    className="accent-amber-500"
                  />
                  <span>showHoursToComplete</span>
                </label>
                <label className={`flex items-center gap-1.5 cursor-pointer ${showHoursToComplete ? 'text-amber-800 dark:text-amber-300' : 'text-amber-400 dark:text-amber-600 opacity-50 cursor-not-allowed'}`}>
                  <input
                    type="checkbox"
                    checked={devHtcAvailable}
                    disabled={!showHoursToComplete}
                    onChange={e => setDevHtcAvailable(e.target.checked)}
                    className="accent-amber-500"
                  />
                  <span>htcAvailable (HTC data submitted)</span>
                </label>
                {/* Explain the three states inline */}
                <span className="ml-auto text-amber-600 dark:text-amber-400 italic">
                  {!showHoursToComplete && '① Off → hoursVariance, no sub-label'}
                  {showHoursToComplete && !devHtcAvailable && '② On, no HTC → hoursVariance + amber sub-label'}
                  {showHoursToComplete && devHtcAvailable && '③ On + HTC → hoursToComplete + grey sub-label'}
                </span>
              </div>
            )}

            <div key="pm_pulse" className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">

              {/* Tile 1 — My Active Projects */}
              <KpiCard
                title="My Active Projects"
                value={myActiveCount}
                icon={FolderKanban}
                accentVariant="info"
                trend={
                  activeCountDelta !== 0
                    ? { value: activeCountDelta, isPositive: activeCountDelta >= 0 }
                    : undefined
                }
                description="vs last week"
              />

              {/* Tile 2 — Hours Remaining (HTC-aware)
                  Off  → hoursVariance, plain description
                  On + htcAvailable → hoursToComplete, muted sub-label
                  On + no HTC data  → hoursVariance, amber sub-label */}
              <KpiCard
                title="Hours Remaining"
                value={
                  hoursIsNegative
                    ? `−${formatHours(Math.abs(displayHours))} hrs`
                    : `${formatHours(displayHours)} hrs`
                }
                icon={Clock}
                accentVariant={hoursIsNegative ? 'error' : 'success'}
                description={
                  !showHoursToComplete
                    ? (hoursIsNegative ? 'Hours overrun across projects' : 'Remaining budget across projects')
                    : undefined
                }
              >
                {/* Sub-label shown only when showHoursToComplete is On */}
                {htcMode && (
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Hours to complete (HTC)
                  </p>
                )}
                {htcOffNoData && (
                  <p className="text-[11px] text-warning-600 mt-0.5">
                    Budget remaining (no HTC data)
                  </p>
                )}
              </KpiCard>

              {/* Tile 3 — Projects On Track */}
              <KpiCard
                title="Projects On Track"
                value={`${myOnTrackCount} / ${myActiveCount}`}
                icon={allOnTrack ? CheckCircle2 : AlertTriangle}
                accentVariant={
                  allOnTrack
                    ? 'success'
                    : myOnTrackCount < myActiveCount / 2
                      ? 'error'
                      : 'warning'
                }
                trend={
                  onTrackDelta !== 0
                    ? { value: onTrackDelta, isPositive: onTrackDelta >= 0 }
                    : undefined
                }
                description={
                  allOnTrack
                    ? 'All projects healthy'
                    : `${myActiveCount - myOnTrackCount} need attention`
                }
              />

              {/* Tile 4 — Pending Actions */}
              {!isPulseTileHiddenForRelease('pending_actions') && (
                <KpiCard
                  title="Pending Actions"
                  value={myOverdueMilestoneCount}
                  icon={hasOverdue ? AlertCircle : CheckCircle2}
                  accentVariant={
                    hasOverdue
                      ? myOverdueMilestoneCount >= 3
                        ? 'error'
                        : 'warning'
                      : 'success'
                  }
                  trend={
                    overdueDelta !== 0
                      ? { value: overdueDelta, isPositive: overdueDelta <= 0 }
                      : undefined
                  }
                  description={
                    hasOverdue
                      ? `Overdue billing milestone${myOverdueMilestoneCount !== 1 ? 's' : ''}`
                      : 'No overdue billing milestones'
                  }
                />
              )}

              {/* Tile 5 — My Portfolio EAC
                  Mock: £487,500 — Σ calculateEAC() across PM's active projects.
                  Colour: green ≤ budget, amber within 10% over, red >10% over.
                  EAC method badge reads from EacSettingsContext (reactive). */}
              <KpiCard
                title="My Portfolio EAC"
                value={`£${(MOCK_PM_PULSE.myPortfolioEac / 1000).toFixed(0)}k`}
                icon={Banknote}
                accentVariant={eacAccent}
              >
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground mt-0.5">
                  {eacMethodDisplay}
                </span>
              </KpiCard>

            </div>
          </>
        );
      }

      // ── My Projects Health overview — F22 ──────────────────────────────────
      // PRD §4, Insight 2 — Homepage overview panel (not a table/list).
      // Shows: RAG breakdown bar, "needs attention" project cards (red/amber only),
      // footer stats, and a CTA to the full project list view.
      case 'my_projects_health': {
        // ── Counts ────────────────────────────────────────────────────────────
        const redProjects   = pmActiveProjects.filter(p => p.health === 'red');
        const amberProjects = pmActiveProjects.filter(p => p.health === 'amber');
        const greenCount    = pmActiveProjects.filter(p => p.health === 'green').length;
        const total         = pmActiveProjects.length;

        // Projects that need attention — red first, then amber, cap at 4 cards
        const attentionProjects = [
          ...redProjects,
          ...amberProjects,
        ].slice(0, 4);

        const hrsOverrunCount = pmActiveProjects.filter(p => (p.hoursVariance ?? 0) < 0).length;

        // ── Primary signal per attention project ──────────────────────────────
        function getPrimarySignal(project: typeof pmActiveProjects[0]): { label: string; colour: string } {
          const nextBillingEvent = nextBillingEventByProject.get(project.id);

          if ((project.costVariancePercent ?? 0) < -10) {
            return { label: 'Over Budget', colour: 'destructive' };
          }
          if (project.forecastEndDate && project.endDate && project.forecastEndDate > project.endDate) {
            return { label: 'Running Over', colour: 'warning' };
          }
          if (
            project.budgetHours > 0 &&
            (project.hoursVariance ?? 0) < project.budgetHours * 0.1 &&
            (project.hoursVariance ?? 0) >= 0
          ) {
            return { label: 'Hours Critical', colour: 'warning' };
          }
          if (nextBillingEvent) {
            const daysAway = (new Date(nextBillingEvent).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
            if (daysAway >= 0 && daysAway <= 7) {
              return { label: 'Billing Due Soon', colour: 'purple' };
            }
          }
          return { label: project.health === 'red' ? 'Off Track' : 'At Risk', colour: project.health === 'red' ? 'destructive' : 'warning' };
        }

        // ── Empty state ───────────────────────────────────────────────────────
        if (total === 0) {
          return (
            <Card key="my_projects_health">
              <CardHeader>
                <CardTitle className="text-base">My Projects Health</CardTitle>
                <CardDescription>Overall health across your active projects</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
                  <FileX className="h-10 w-10 opacity-30" />
                  <p className="text-sm font-medium">No active projects</p>
                  <p className="text-xs">Projects you manage will appear here once they are active.</p>
                  <Button variant="outline" size="sm" onClick={() => handleNavigate('projects')} className="mt-2 gap-1">
                    View all projects <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        }

        // ── All-clear state — everything green ────────────────────────────────
        const allGreen = redProjects.length === 0 && amberProjects.length === 0;

        return (
          <Card key="my_projects_health">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">My Projects Health</CardTitle>
                  <CardDescription className="mt-0.5">
                    Overall health across {total} active project{total !== 1 ? 's' : ''}
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleNavigate('projects')}
                  className="gap-1 text-xs text-muted-foreground"
                >
                  View all projects <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-5">

              {/* ── RAG summary bar + counts ─────────────────────────────── */}
              <div className="space-y-2">
                {/* Segmented bar */}
                <div className="flex h-3 w-full rounded-full overflow-hidden gap-px">
                  {redProjects.length > 0 && (
                    <div
                      className="bg-destructive h-full transition-all"
                      style={{ width: `${(redProjects.length / total) * 100}%` }}
                    />
                  )}
                  {amberProjects.length > 0 && (
                    <div
                      className="bg-warning-500 h-full transition-all"
                      style={{ width: `${(amberProjects.length / total) * 100}%` }}
                    />
                  )}
                  {greenCount > 0 && (
                    <div
                      className="bg-success-500 h-full transition-all"
                      style={{ width: `${(greenCount / total) * 100}%` }}
                    />
                  )}
                </div>

                {/* Legend row */}
                <div className="flex items-center gap-5 text-xs text-muted-foreground">
                  {redProjects.length > 0 && (
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block h-2 w-2 rounded-full bg-destructive shrink-0" />
                      <span className="font-medium text-destructive">{redProjects.length}</span> Off Track
                    </span>
                  )}
                  {amberProjects.length > 0 && (
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block h-2 w-2 rounded-full bg-warning-500 shrink-0" />
                      <span className="font-medium text-warning-600">{amberProjects.length}</span> At Risk
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-success-500 shrink-0" />
                    <span className="font-medium text-success-600">{greenCount}</span> On Track
                  </span>
                  {hrsOverrunCount > 0 && (
                    <span className="flex items-center gap-1.5 ml-auto">
                      <TrendingDown className="h-3 w-3 text-destructive shrink-0" />
                      {hrsOverrunCount} hours overrun
                    </span>
                  )}
                </div>
              </div>

              {/* ── All-clear banner ────────────────────────────────────────── */}
              {allGreen && (
                <div className="flex items-center gap-3 rounded-lg bg-success-50 border border-success-200 px-4 py-3">
                  <CheckCircle2 className="h-5 w-5 text-success-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-success-700">All projects on track</p>
                    <p className="text-xs text-success-600 mt-0.5">No issues detected across your {total} active project{total !== 1 ? 's' : ''}.</p>
                  </div>
                </div>
              )}

              {/* ── Needs attention cards (red + amber only) ─────────────────── */}
              {!allGreen && attentionProjects.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Needs Attention
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {attentionProjects.map(project => {
                      const signal = getPrimarySignal(project);
                      const isRed  = project.health === 'red';
                      const signalColour: Record<string, string> = {
                        destructive: 'bg-destructive/10 text-destructive border-destructive/20',
                        warning:     'bg-warning-100 text-warning-700 border-warning-200',
                        purple:      'bg-purple-100 text-purple-700 border-purple-200',
                      };

                      return (
                        <button
                          key={project.id}
                          onClick={() => handleNavigate(`projects/${project.id}`)}
                          className={cn(
                            'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/40 w-full',
                            isRed
                              ? 'border-destructive/20 bg-destructive/5'
                              : 'border-warning-200 bg-warning-50/50',
                          )}
                        >
                          {/* RAG dot */}
                          <span
                            className={cn(
                              'mt-0.5 h-2.5 w-2.5 rounded-full shrink-0',
                              isRed ? 'bg-destructive' : 'bg-warning-500',
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium leading-snug truncate">{project.name}</p>
                            {project.clientNames && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">{project.clientNames}</p>
                            )}
                          </div>
                          <Badge
                            variant="outline"
                            className={cn('text-xs font-medium whitespace-nowrap shrink-0', signalColour[signal.colour] ?? 'bg-muted text-muted-foreground')}
                          >
                            {signal.label}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>

                  {/* Overflow hint — if there are more attention projects than shown */}
                  {(redProjects.length + amberProjects.length) > 4 && (
                    <button
                      onClick={() => handleNavigate('projects')}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 pt-0.5"
                    >
                      +{(redProjects.length + amberProjects.length) - 4} more project{(redProjects.length + amberProjects.length) - 4 !== 1 ? 's' : ''} need attention
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}

              {/* ── EAC summary table ────────────────────────────────────────── */}
              {/* Mock data — Σ calculateEAC() per project; wired to live EAC when API ready */}
              {(() => {
                const MOCK_EAC_ROWS = [
                  { name: 'Tension Table Rental',        health: 'red'   as const, eacCost: 142500, budget: 120000, eacVariance: 22500,  eacVariancePct: 18.8  },
                  { name: 'Pipeline Inspection Phase 2', health: 'amber' as const, eacCost: 98000,  budget: 90000,  eacVariance: 8000,   eacVariancePct: 8.9   },
                  { name: 'Subsea Survey Q3',            health: 'amber' as const, eacCost: 210000, budget: 205000, eacVariance: 5000,   eacVariancePct: 2.4   },
                  { name: 'Riser Analysis 2026',         health: 'green' as const, eacCost: 67000,  budget: 75000,  eacVariance: -8000,  eacVariancePct: -10.7 },
                ];

                function getEacVariantClass(pct: number): string {
                  if (pct <= 0) return 'bg-success-100 text-success-700 border-success-200';
                  if (pct <= 10) return 'bg-warning-100 text-warning-700 border-warning-200';
                  return 'bg-destructive/10 text-destructive border-destructive/20';
                }

                function getHealthDot(health: 'red' | 'amber' | 'green'): string {
                  if (health === 'red')   return 'bg-destructive';
                  if (health === 'amber') return 'bg-warning-500';
                  return 'bg-success-500';
                }

                return (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      EAC Summary
                    </p>
                    <div className="rounded-md border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/40">
                            <TableHead className="text-xs py-2 pl-3">Project</TableHead>
                            <TableHead className="text-xs py-2 text-right">EAC Cost</TableHead>
                            <TableHead className="text-xs py-2 pr-3 text-right">EAC vs Budget</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {MOCK_EAC_ROWS.map((row) => (
                            <TableRow key={row.name} className="hover:bg-muted/20">
                              <TableCell className="py-2 pl-3">
                                <div className="flex items-center gap-2">
                                  <span className={cn('h-2 w-2 rounded-full shrink-0', getHealthDot(row.health))} />
                                  <span className="text-xs font-medium truncate max-w-[180px]">{row.name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="py-2 text-xs text-right tabular-nums">
                                £{row.eacCost.toLocaleString()}
                              </TableCell>
                              <TableCell className="py-2 pr-3 text-right">
                                <span className={cn(
                                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums',
                                  getEacVariantClass(row.eacVariancePct),
                                )}>
                                  {row.eacVariance > 0 ? '+' : ''}£{Math.abs(row.eacVariance).toLocaleString()}
                                  <span className="opacity-70">({row.eacVariancePct > 0 ? '+' : ''}{row.eacVariancePct.toFixed(1)}%)</span>
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                );
              })()}

            </CardContent>
          </Card>
        );
      }

      // ── Budget & Hours Burn — F23 ───────────────────────────────────────────
      // PRD §4, Insight 3 — horizontal burn bars per project, Cost/Hours tab toggle,
      // completion diamond overlay, OVER badge for >100% burn.
      case 'budget_hours_burn': {
        // Build per-project burn rows — sorted OVER first, then by burn % desc
        const burnRows = pmActiveProjects
            .map(p => {
              // Cost burn
              const costBudget   = p.budgetCost ?? ((p.budgetLabour ?? 0) + (p.budgetExpense ?? 0));
              const actualCost   = p.actualCosts ?? 0;
              const costBurnPct  = costBudget > 0 ? (actualCost / costBudget) * 100 : 0;
              const costOverPct  = Math.max(0, costBurnPct - 100);

              // Hours burn
              const hoursBudget   = p.budgetHours ?? 0;
              const actualHours   = p.actualHours ?? 0;
              const hoursBurnPct  = hoursBudget > 0 ? (actualHours / hoursBudget) * 100 : 0;
              const hoursOverPct  = Math.max(0, hoursBurnPct - 100);

              const burnPct   = burnTab === 'cost' ? costBurnPct  : hoursBurnPct;
              const overPct   = burnTab === 'cost' ? costOverPct  : hoursOverPct;
              const isOver    = burnPct > 100;
              const completionPct = p.completionPercent ?? 0;
              const isManual  = (p as { completionPercentMode?: string }).completionPercentMode === 'manual';

              return {
                id: p.id,
                name: p.name,
                clientNames: p.clientNames ?? '',
                costBudget,
                actualCost,
                costBurnPct,
                costOverPct,
                hoursBudget,
                actualHours,
                hoursBurnPct,
                hoursOverPct,
                burnPct,
                overPct,
                isOver,
                completionPct,
                isManual,
              };
            })
            // OVER projects first; within each group sort by burn % desc
            .sort((a, b) => {
              if (a.isOver !== b.isOver) return a.isOver ? -1 : 1;
              return b.burnPct - a.burnPct;
            });

        const getBurnColour = (pct: number) => {
          if (pct > 100) return 'bg-destructive';
          if (pct > 90)  return 'bg-error-500';
          if (pct > 70)  return 'bg-warning-500';
          return 'bg-success-500';
        };

        const getBurnTextColour = (pct: number) => {
          if (pct > 100) return 'text-destructive font-bold';
          if (pct > 90)  return 'text-error-600 font-semibold';
          if (pct > 70)  return 'text-warning-600';
          return 'text-success-600';
        };

        if (pmActiveProjects.length === 0) {
          return (
            <Card key="budget_hours_burn">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  Budget &amp; Hours Burn
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
                  <BarChart3 className="h-10 w-10 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">No active projects to display</p>
                </div>
              </CardContent>
            </Card>
          );
        }

        return (
          <Card key="budget_hours_burn">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    Budget &amp; Hours Burn
                  </CardTitle>
                  <CardDescription className="mt-0.5">
                    Sorted by burn % — highest risk first
                  </CardDescription>
                </div>
                {/* Cost / Hours tab toggle */}
                <div className="flex items-center rounded-md border bg-muted/30 p-0.5 gap-0.5 shrink-0">
                  <button
                    onClick={() => setBurnTab('cost')}
                    className={cn(
                      'flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors',
                      burnTab === 'cost'
                        ? 'bg-background shadow text-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <DollarSign className="h-3 w-3" />
                    Cost Burn
                  </button>
                  <button
                    onClick={() => setBurnTab('hours')}
                    className={cn(
                      'flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors',
                      burnTab === 'hours'
                        ? 'bg-background shadow text-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Clock className="h-3 w-3" />
                    Hours Burn
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="space-y-3">
                {burnRows.slice(0, 5).map(row => {
                  // Clamp bar fill to 100% max — overflow shown separately
                  const fillPct  = Math.min(row.burnPct, 100);
                  const emptyPct = Math.max(0, 100 - fillPct);
                  // Diamond position: completionPct along the full bar width (capped at 100)
                  const diamondLeft = Math.min(row.completionPct, 100);

                  return (
                    <div
                      key={row.id}
                      className="group cursor-pointer rounded-md p-2 hover:bg-muted/40 transition-colors"
                      onClick={() => handleNavigate(`projects/${row.id}?tab=cost-budget`)}
                    >
                      {/* Project label row */}
                      <div className="flex items-center justify-between mb-1.5 gap-2">
                        <div className="min-w-0">
                          <span className="text-xs font-semibold truncate block">{row.name}</span>
                          {row.clientNames && (
                            <span className="text-[10px] text-muted-foreground truncate block">
                              {row.clientNames}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {row.isOver && (
                            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                              OVER
                            </Badge>
                          )}
                          <span className={cn('text-xs tabular-nums', getBurnTextColour(row.burnPct))}>
                            {row.burnPct.toFixed(0)}%
                          </span>
                          {burnTab === 'cost' ? (
                            <span className="text-[10px] text-muted-foreground tabular-nums">
                              £{(row.actualCost / 1000).toFixed(0)}k / £{(row.costBudget / 1000).toFixed(0)}k
                            </span>
                          ) : (
                            <span className="text-[10px] text-muted-foreground tabular-nums">
                              {formatHours(row.actualHours)}h / {formatHours(row.hoursBudget)}h
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Burn bar with diamond overlay */}
                      <div className="relative">
                        {/* Bar track */}
                        <div className="flex h-4 w-full rounded overflow-hidden bg-muted">
                          {/* Actual burn fill */}
                          <div
                            className={cn('h-full transition-all', getBurnColour(row.burnPct))}
                            style={{ width: `${fillPct}%` }}
                          />
                          {/* Remaining budget (light fill) */}
                          <div
                            className="h-full bg-muted-foreground/10"
                            style={{ width: `${emptyPct}%` }}
                          />
                        </div>

                        {/* Over-budget overflow indicator (thin red strip on right edge) */}
                        {row.isOver && (
                          <div
                            className="absolute top-0 right-0 h-full bg-destructive/30 rounded-r"
                            style={{ width: `${Math.min(row.overPct, 30)}%` }}
                            title={`${row.overPct.toFixed(0)}% over budget`}
                          />
                        )}

                        {/* Completion % diamond overlay */}
                        {row.completionPct > 0 && (
                          <div
                            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10"
                            style={{ left: `${diamondLeft}%` }}
                            title={
                              row.isManual
                                ? `${row.completionPct}% complete (manually set)`
                                : `${row.completionPct}% complete`
                            }
                          >
                            <Diamond
                              className={cn(
                                'h-3 w-3 fill-current drop-shadow',
                                row.burnPct > row.completionPct
                                  ? 'text-error-600'
                                  : 'text-success-600',
                              )}
                            />
                            {row.isManual && (
                              <span className="absolute -top-1 -right-1.5 text-[8px] font-bold text-muted-foreground leading-none">
                                *
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Burn vs completion signal */}
                      {row.completionPct > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          {row.burnPct > row.completionPct + 10 ? (
                            <>
                              <TrendingUp className="h-2.5 w-2.5 text-error-500" />
                              <span className="text-[10px] text-error-600">
                                Burn ahead of completion by {(row.burnPct - row.completionPct).toFixed(0)}%
                              </span>
                            </>
                          ) : row.burnPct < row.completionPct - 10 ? (
                            <>
                              <TrendingDown className="h-2.5 w-2.5 text-success-500" />
                              <span className="text-[10px] text-success-600">
                                Completion ahead of burn — efficient delivery
                              </span>
                            </>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">
                              Burn tracking with completion
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Overflow link — only shown when there are more than 5 projects */}
              {burnRows.length > 5 && (
                <button
                  onClick={() => handleNavigate('projects')}
                  className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors w-full justify-center py-1"
                >
                  +{burnRows.length - 5} more projects — View all
                  <ChevronRight className="h-3 w-3" />
                </button>
              )}

              {/* Legend */}
              <div className="mt-4 pt-3 border-t flex items-center gap-4 text-[10px] text-muted-foreground flex-wrap">
                <div className="flex items-center gap-1">
                  <div className="h-2.5 w-2.5 rounded-sm bg-success-500" />
                  <span>&lt;70% burn</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-2.5 w-2.5 rounded-sm bg-warning-500" />
                  <span>70–90% burn</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-2.5 w-2.5 rounded-sm bg-error-500" />
                  <span>90–100%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-2.5 w-2.5 rounded-sm bg-destructive" />
                  <span>Over budget</span>
                </div>
                <div className="flex items-center gap-1">
                  <Diamond className="h-2.5 w-2.5 fill-current text-muted-foreground" />
                  <span>◆ Completion %{' '}</span>
                  <span className="italic">(* = manually set)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      }

      // ── Schedule Health — F24 ───────────────────────────────────────────────
      case 'schedule_health': {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // ── Rich mode: SQL-aggregated schedule health (single API call) ────
        if (isPlannerConnected || isLiveMode) {
          const total = scheduleHealthData?.totalProjects ?? pmActiveProjects.length;
          const criticalCount = scheduleHealthData?.criticalCount ?? 0;
          const warningCount = scheduleHealthData?.warningCount ?? 0;
          const healthyCount = scheduleHealthData?.healthyCount ?? total;
          const unmetRolesTotal = scheduleHealthData?.unmetRolesTotal ?? 0;
          const attentionRows = scheduleHealthData?.attentionProjects ?? [];
          const shownAttention = attentionRows.slice(0, 4);
          const extraAttention = attentionRows.length - shownAttention.length;

          return (
            <Card key="schedule_health">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">Schedule Health</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    {plannerStatus.productLabel && (
                      <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                        {plannerStatus.productLabel}
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                      onClick={() => handleNavigate('scheduling')}
                    >
                      View all <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <CardDescription className="text-xs">
                  Scheduling coverage and warnings across your active projects
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-4">
                {schedulingLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                  </div>
                ) : total === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                    <FileX className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm font-medium text-muted-foreground">No active projects</p>
                    <p className="text-xs text-muted-foreground mt-1">Scheduling health will appear when you have active projects</p>
                  </div>
                ) : (
                  <>
                    {/* ── Tier 1: Summary strip ── */}
                    <div className="flex items-center gap-4 rounded-lg border bg-muted/30 px-4 py-3 mb-4">
                      <div className="flex flex-col items-center min-w-[36px]">
                        <span className="text-lg font-bold leading-none">{total}</span>
                        <span className="text-[10px] text-muted-foreground mt-0.5">Projects</span>
                      </div>
                      <div className="w-px h-8 bg-border" />
                      <div className="flex items-center gap-3 flex-1 flex-wrap">
                        {criticalCount > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-block h-2 w-2 rounded-full bg-error-500 shrink-0" />
                            <span className="text-xs font-medium text-error-600 dark:text-error-400">{criticalCount} critical</span>
                          </div>
                        )}
                        {warningCount > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-block h-2 w-2 rounded-full bg-warning-500 shrink-0" />
                            <span className="text-xs font-medium text-warning-600 dark:text-warning-400">{warningCount} at risk</span>
                          </div>
                        )}
                        {healthyCount > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-block h-2 w-2 rounded-full bg-success-500 shrink-0" />
                            <span className="text-xs text-muted-foreground">{healthyCount} healthy</span>
                          </div>
                        )}
                      </div>
                      {unmetRolesTotal > 0 && (
                        <div className="flex items-center gap-1 text-xs text-error-600 dark:text-error-400 shrink-0">
                          <ShieldAlert className="h-3 w-3" />
                          <span>{unmetRolesTotal} unmet role{unmetRolesTotal !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>

                    {/* ── Tier 2: Attention cards or all-clear ── */}
                    {attentionRows.length === 0 ? (
                      <div className="flex items-center gap-2 rounded-lg bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-4 py-3">
                        <CheckCircle2 className="h-4 w-4 text-success-600 dark:text-success-400 shrink-0" />
                        <p className="text-xs text-success-700 dark:text-success-300 font-medium">All projects have healthy scheduling coverage</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Needs attention</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {shownAttention.map((proj) => {
                            const isCritical = proj.healthStatus === 'critical';
                            const signal = proj.coveragePercent < 100
                              ? `${proj.coveragePercent}% coverage`
                              : proj.unmetRoles > 0
                                ? `${proj.unmetRoles} unmet role${proj.unmetRoles !== 1 ? 's' : ''}`
                                : 'Review needed';
                            return (
                              <button
                                key={proj.projectId}
                                className={cn(
                                  'text-left rounded-lg border px-3 py-2.5 transition-colors hover:bg-muted/50',
                                  isCritical
                                    ? 'border-error-200 dark:border-error-800 bg-error-50/50 dark:bg-error-950/10'
                                    : 'border-warning-200 dark:border-warning-800 bg-warning-50/50 dark:bg-warning-950/10',
                                )}
                                onClick={() => handleNavigate(`projects/${proj.projectId}?tab=resourcing`)}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium leading-tight truncate">{proj.projectName}</p>
                                    <p className="text-xs text-muted-foreground truncate">{proj.clientNames ?? 'No client'}</p>
                                  </div>
                                  <span className={cn(
                                    'text-xs font-semibold shrink-0',
                                    proj.coveragePercent < 50 ? 'text-error-600 dark:text-error-400' :
                                    proj.coveragePercent < 95 ? 'text-warning-600 dark:text-warning-400' :
                                                    'text-success-600 dark:text-success-400',
                                  )}>
                                    {proj.coveragePercent}%
                                  </span>
                                </div>
                                <p className={cn(
                                  'text-xs mt-1 truncate',
                                  isCritical ? 'text-error-600 dark:text-error-400' : 'text-warning-600 dark:text-warning-400',
                                )}>
                                  {signal}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                        {extraAttention > 0 && (
                          <button
                            className="mt-2 w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                            onClick={() => handleNavigate('scheduling')}
                          >
                            +{extraAttention} more project{extraAttention !== 1 ? 's' : ''} need attention →
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        }

        // ── Basic mode: Two-tier overview (summary strip + attention cards) ──
        {
          const basicRows = pmActiveProjects
            .map((p) => {
              const end = p.endDate ? new Date(p.endDate) : null;
              const forecast = p.forecastEndDate ? new Date(p.forecastEndDate) : null;
              const daysRemaining = end ? Math.ceil((end.getTime() - today.getTime()) / 86_400_000) : null;
              const isOverrun = forecast && end ? forecast > end : false;
              const overrunDays = isOverrun && forecast && end
                ? Math.ceil((forecast.getTime() - end.getTime()) / 86_400_000)
                : 0;
              return { p, end, forecast, daysRemaining, isOverrun, overrunDays };
            });

          const total      = basicRows.length;
          const redCount   = basicRows.filter(r => r.p.health === 'red').length;
          const amberCount = basicRows.filter(r => r.p.health === 'amber').length;
          const greenCount = total - redCount - amberCount;
          const overrunCount = basicRows.filter(r => r.isOverrun).length;

          // Attention: red + amber, sorted red-first then least days remaining
          const attentionRows = basicRows
            .filter(r => r.p.health === 'red' || r.p.health === 'amber' || r.isOverrun || (r.daysRemaining !== null && r.daysRemaining < 14))
            .sort((a, b) => {
              const order: Record<string, number> = { red: 0, amber: 1, green: 2 };
              const ao = order[a.p.health ?? 'green'] ?? 2;
              const bo = order[b.p.health ?? 'green'] ?? 2;
              if (ao !== bo) return ao - bo;
              return (a.daysRemaining ?? 9999) - (b.daysRemaining ?? 9999);
            });
          const shownAttention = attentionRows.slice(0, 4);
          const extraAttention = attentionRows.length - shownAttention.length;

          return (
            <Card key="schedule_health">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">Schedule Health</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                      Dates Only
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                      onClick={() => handleNavigate('portfolio')}
                    >
                      View all <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <CardDescription className="text-xs">
                  Contracted vs forecast end dates across your active projects
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-4">
                {pmActiveProjects.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                    <FileX className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm font-medium text-muted-foreground">No active projects</p>
                  </div>
                ) : (
                  <>
                    {/* ── Tier 1: Summary strip ── */}
                    <div className="flex items-center gap-4 rounded-lg border bg-muted/30 px-4 py-3 mb-4">
                      <div className="flex flex-col items-center min-w-[36px]">
                        <span className="text-lg font-bold leading-none">{total}</span>
                        <span className="text-[10px] text-muted-foreground mt-0.5">Projects</span>
                      </div>
                      <div className="w-px h-8 bg-border" />
                      <div className="flex items-center gap-3 flex-1 flex-wrap">
                        {redCount > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-block h-2 w-2 rounded-full bg-error-500 shrink-0" />
                            <span className="text-xs font-medium text-error-600 dark:text-error-400">{redCount} off track</span>
                          </div>
                        )}
                        {amberCount > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-block h-2 w-2 rounded-full bg-warning-500 shrink-0" />
                            <span className="text-xs font-medium text-warning-600 dark:text-warning-400">{amberCount} at risk</span>
                          </div>
                        )}
                        {greenCount > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-block h-2 w-2 rounded-full bg-success-500 shrink-0" />
                            <span className="text-xs text-muted-foreground">{greenCount} on track</span>
                          </div>
                        )}
                      </div>
                      {overrunCount > 0 && (
                        <div className="flex items-center gap-1 text-xs text-error-600 dark:text-error-400 shrink-0">
                          <TrendingDown className="h-3 w-3" />
                          <span>{overrunCount} overrun</span>
                        </div>
                      )}
                    </div>

                    {/* Connect planner nudge */}
                    <div className="flex items-center gap-2 rounded-md bg-muted/40 border px-3 py-2 mb-4">
                      <WifiOff className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <p className="text-xs text-muted-foreground">
                        Connect a planning tool for booking coverage and scheduling warnings.{' '}
                        <button
                          className="underline underline-offset-2 hover:text-foreground transition-colors"
                          onClick={() => handleNavigate('settings/integrations')}
                        >
                          Connect now
                        </button>
                      </p>
                    </div>

                    {/* ── Tier 2: Attention cards or all-clear ── */}
                    {attentionRows.length === 0 ? (
                      <div className="flex items-center gap-2 rounded-lg bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-4 py-3">
                        <CheckCircle2 className="h-4 w-4 text-success-600 dark:text-success-400 shrink-0" />
                        <p className="text-xs text-success-700 dark:text-success-300 font-medium">All projects are on track</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Needs attention</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {shownAttention.map(({ p, end, daysRemaining, isOverrun, overrunDays }) => {
                            const isCritical = p.health === 'red' || (daysRemaining !== null && daysRemaining < 0);
                            const signal = p.health === 'red'
                              ? 'Off Track'
                              : isOverrun
                                ? `+${overrunDays}d forecast overrun`
                                : daysRemaining !== null && daysRemaining < 14
                                  ? daysRemaining < 0 ? `Ended ${Math.abs(daysRemaining)}d ago` : `${daysRemaining}d remaining`
                                  : 'At Risk';
                            return (
                              <button
                                key={p.id}
                                className={cn(
                                  'text-left rounded-lg border px-3 py-2.5 transition-colors hover:bg-muted/50',
                                  isCritical
                                    ? 'border-error-200 dark:border-error-800 bg-error-50/50 dark:bg-error-950/10'
                                    : 'border-warning-200 dark:border-warning-800 bg-warning-50/50 dark:bg-warning-950/10',
                                )}
                                onClick={() => handleNavigate(`projects/${p.id}`)}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium leading-tight truncate">{p.name}</p>
                                    <p className="text-xs text-muted-foreground truncate">{p.clientNames ?? 'No client'}</p>
                                  </div>
                                  {end && (
                                    <span className="text-xs text-muted-foreground shrink-0">
                                      {end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                    </span>
                                  )}
                                </div>
                                <p className={cn(
                                  'text-xs mt-1 truncate',
                                  isCritical ? 'text-error-600 dark:text-error-400' : 'text-warning-600 dark:text-warning-400',
                                )}>
                                  {signal}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                        {extraAttention > 0 && (
                          <button
                            className="mt-2 w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                            onClick={() => handleNavigate('portfolio')}
                          >
                            +{extraAttention} more project{extraAttention !== 1 ? 's' : ''} need attention →
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        }
      }

      // ── My Team This Week — F25 ─────────────────────────────────────────────
      // PRD §4, Insight 5 — Mode-aware widget.
      //
      // Rich mode  (planner connected): booking status per person from
      //   ResourceAllocation records active this week; status derived from
      //   allocation.status (confirmed / tentative) or absent (no booking).
      //   Columns: Name | Role | Project | Booking Status | Allocated Hrs | Status
      //   Summary strip: Confirmed / Tentative / No Booking counts.
      //
      // Basic mode (no planner): uses ResourceAllocation + UtilisationData
      //   to derive three summary tiles: Total Team | Over Allocated | At Risk.
      //   Shows a per-person list with statuses derived from allocation %.
      //
      // Sort (both modes): No Booking → Over Allocated → At Risk → On Track.
      // Max 15 rows shown — "View full team" link if more.
      // Positive all-clear state when everyone is confirmed / on track.
      //
      // Data note: In live mode, team data is derived from fetchResourceAllocations
      //   and fetchEmployees (backend API). In demo/tour, falls back to mock data.
      case 'my_team_this_week': {
        // ── Week boundary helpers ─────────────────────────────────────────────
        const now = new Date();
        const weekStart = new Date(now);
        weekStart.setHours(0, 0, 0, 0);
        weekStart.setDate(now.getDate() - (now.getDay() === 0 ? 6 : now.getDay() - 1)); // Monday
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Sunday

        /** True if allocation overlaps the current Mon–Sun week */
        function overlapsThisWeek(start: string, end: string): boolean {
          const s = new Date(start);
          const e = new Date(end);
          s.setHours(0, 0, 0, 0);
          e.setHours(23, 59, 59, 999);
          return s <= weekEnd && e >= weekStart;
        }

        // ── Build the team rows ───────────────────────────────────────────────
        // Collect all ResourceAllocation records for PM's active projects that
        // overlap the current week. One row per person-per-project (no merging).
        // Uses live API data in live mode, mock data in demo/tour.
        interface TeamRow {
          key: string;
          employeeId: number;
          employeeName: string;
          role: string;
          projectId: number;
          projectName: string;
          bookingStatus: 'confirmed' | 'tentative' | 'no_booking';
          allocatedHours: number;
          actualHours: number;
          derivedStatus: 'On Track' | 'At Risk' | 'Over Allocated';
        }

        const teamRows: TeamRow[] = [];

        for (const project of pmActiveProjects) {
          const projectAllocations = getProjectAllocations(project.id).filter(a =>
            overlapsThisWeek(a.startDate, a.endDate),
          );

          // Collect allocated employee IDs for this project (for gap-fill below)
          const allocatedEmployeeIds = new Set(projectAllocations.map(a => a.employeeId));

          for (const alloc of projectAllocations) {
            const emp = findEmployee(alloc.employeeId);
            const allocPct = alloc.allocationPercent ?? 0;
            // Derive weekly hours from allocation % (assuming 40hr week)
            const allocHrs = alloc.allocatedHours ?? Math.round((allocPct / 100) * 40);
            // Actual hours — approximate from utilisation data
            const utilisation = (alloc as { utilisationPercent?: number }).utilisationPercent ?? allocPct;
            const actualHrs = Math.round((utilisation / 100) * 40);

            // Derived status
            const derivedStatus: 'On Track' | 'At Risk' | 'Over Allocated' =
              allocPct > 100 ? 'Over Allocated'
              : allocPct >= 90 ? 'At Risk'
              : 'On Track';

            teamRows.push({
              key: `${project.id}-${alloc.employeeId}`,
              employeeId: alloc.employeeId,
              employeeName: emp?.name ?? alloc.employeeName ?? `Employee ${alloc.employeeId}`,
              role: emp?.role ?? alloc.role ?? alloc.roleName ?? 'Unknown',
              projectId: project.id,
              projectName: project.name,
              bookingStatus: alloc.status === 'tentative' ? 'tentative' : 'confirmed',
              allocatedHours: allocHrs,
              actualHours: actualHrs,
              derivedStatus,
            });
          }

          // Rich mode gap-fill: find team members on this project with
          // role assignments but NO allocation overlapping this week.
          // We only do this for projects where ≥1 allocation exists (project has a team).
          if (isPlannerConnected && projectAllocations.length > 0) {
            const roleAssignments = project.roleAssignments ?? [];
            for (const ra of roleAssignments) {
              if (!allocatedEmployeeIds.has(ra.employeeId)) {
                const emp = findEmployee(ra.employeeId);
                teamRows.push({
                  key: `${project.id}-${ra.employeeId}-nobooking`,
                  employeeId: ra.employeeId,
                  employeeName: ra.employeeName,
                  role: emp?.role ?? PROJECT_ROLE_LABELS[ra.role] ?? String(ra.role),
                  projectId: project.id,
                  projectName: project.name,
                  bookingStatus: 'no_booking',
                  allocatedHours: 0,
                  actualHours: 0,
                  derivedStatus: 'At Risk',
                });
              }
            }
          }
        }

        // ── Sort: No Booking → Over Allocated → At Risk → On Track ──────────
        const bookingOrder: Record<string, number> = { no_booking: 0, confirmed: 2, tentative: 1 };
        const statusOrder: Record<string, number> = { 'Over Allocated': 0, 'At Risk': 1, 'On Track': 2 };

        teamRows.sort((a, b) => {
          const bookingDiff = bookingOrder[a.bookingStatus] - bookingOrder[b.bookingStatus];
          if (bookingDiff !== 0) return bookingDiff;
          const statusDiff = statusOrder[a.derivedStatus] - statusOrder[b.derivedStatus];
          if (statusDiff !== 0) return statusDiff;
          return a.employeeName.localeCompare(b.employeeName);
        });

        // ── Summary counts ────────────────────────────────────────────────────
        const confirmedCount    = teamRows.filter(r => r.bookingStatus === 'confirmed').length;
        const tentativeCount    = teamRows.filter(r => r.bookingStatus === 'tentative').length;
        const noBookingCount    = teamRows.filter(r => r.bookingStatus === 'no_booking').length;
        const overAllocCount    = teamRows.filter(r => r.derivedStatus === 'Over Allocated').length;
        const atRiskCount       = teamRows.filter(r => r.derivedStatus === 'At Risk').length;
        const totalTeamCount    = teamRows.length;
        const allGood           = totalTeamCount > 0 && noBookingCount === 0 && overAllocCount === 0;

        const MAX_ROWS = 15;
        const visibleRows     = teamRows.slice(0, MAX_ROWS);
        const hasMore         = teamRows.length > MAX_ROWS;

        // ── Booking-status badge helper ───────────────────────────────────────
        function BookingStatusBadge({ status }: { status: TeamRow['bookingStatus'] }) {
          if (status === 'confirmed') {
            return (
              <Badge variant="outline" className="text-xs border-success-200 text-success-700 bg-success-50 gap-1 whitespace-nowrap">
                <UserCheck className="h-3 w-3" />
                Confirmed
              </Badge>
            );
          }
          if (status === 'tentative') {
            return (
              <Badge variant="outline" className="text-xs border-warning-200 text-warning-700 bg-warning-50 gap-1 whitespace-nowrap">
                <UserMinus className="h-3 w-3" />
                Tentative
              </Badge>
            );
          }
          return (
            <Badge variant="outline" className="text-xs border-destructive/30 text-destructive bg-destructive/5 gap-1 whitespace-nowrap">
              <UserX className="h-3 w-3" />
              No Booking
            </Badge>
          );
        }

        // ── Derived-status badge helper ───────────────────────────────────────
        function StatusBadge({ status }: { status: TeamRow['derivedStatus'] }) {
          if (status === 'Over Allocated') {
            return (
              <Badge variant="outline" className="text-xs border-destructive/30 text-destructive bg-destructive/5 whitespace-nowrap">
                Over Allocated
              </Badge>
            );
          }
          if (status === 'At Risk') {
            return (
              <Badge variant="outline" className="text-xs border-warning-200 text-warning-700 bg-warning-50 whitespace-nowrap">
                At Risk
              </Badge>
            );
          }
          return (
            <Badge variant="outline" className="text-xs border-success-200 text-success-700 bg-success-50 whitespace-nowrap">
              On Track
            </Badge>
          );
        }

        // ── Empty state — no active projects ──────────────────────────────────
        if (pmActiveProjects.length === 0) {
          return (
            <Card key="my_team_this_week">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">My Team This Week</CardTitle>
                </div>
                <CardDescription>No active projects — no team data to display</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                  <Users className="h-10 w-10 opacity-25" />
                  <p className="text-sm font-medium">No active projects</p>
                  <p className="text-xs">Your team members will appear here once you have active projects.</p>
                </div>
              </CardContent>
            </Card>
          );
        }

        // ── All-good positive state ────────────────────────────────────────────
        if (allGood && totalTeamCount > 0) {
          return (
            <Card key="my_team_this_week" className="border-success-200 dark:border-success-800">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-success-600" />
                    <CardTitle className="text-base">My Team This Week</CardTitle>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isPlannerConnected && plannerStatus.productLabel && (
                      <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                        {plannerStatus.productLabel}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                      {weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – {weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 rounded-lg bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-4 py-3">
                  <CheckCircle2 className="h-5 w-5 text-success-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-success-700 dark:text-success-300">
                      Your team is fully booked
                    </p>
                    <p className="text-xs text-success-600 dark:text-success-400">
                      All {totalTeamCount} team member{totalTeamCount !== 1 ? 's' : ''} have confirmed bookings this week.
                    </p>
                  </div>
                </div>
                {/* Still show the summary strip */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <CompactKpiCard
                    title="Confirmed"
                    value={String(confirmedCount)}
                    icon={UserCheck}
                    accentVariant={confirmedCount > 0 ? 'success' : 'info'}
                    className="shadow-none"
                  />
                  <CompactKpiCard
                    title="Tentative"
                    value={String(tentativeCount)}
                    icon={UserMinus}
                    accentVariant={tentativeCount > 0 ? 'warning' : 'info'}
                    className="shadow-none"
                  />
                  <CompactKpiCard
                    title="No Booking"
                    value={String(noBookingCount)}
                    icon={UserX}
                    accentVariant={noBookingCount > 0 ? 'error' : 'success'}
                    className="shadow-none"
                  />
                </div>
              </CardContent>
            </Card>
          );
        }

        // ── Rich mode — planner connected ──────────────────────────────────────
        if (isPlannerConnected) {
          return (
            <Card key="my_team_this_week">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">My Team This Week</CardTitle>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {plannerStatus.productLabel && (
                      <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                        {plannerStatus.productLabel}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                      {weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – {weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </Badge>
                  </div>
                </div>
                <CardDescription className="text-xs">
                  Booking status across {pmActiveProjects.length} active project{pmActiveProjects.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-0 space-y-4">
                {/* ── Summary strip — 3 tiles ─────────────────────────────── */}
                <div className="grid grid-cols-3 gap-3">
                  <CompactKpiCard
                    title="Confirmed"
                    value={String(confirmedCount)}
                    icon={UserCheck}
                    accentVariant={confirmedCount > 0 ? 'success' : 'info'}
                    className="shadow-none"
                  />
                  <CompactKpiCard
                    title="Tentative"
                    value={String(tentativeCount)}
                    icon={UserMinus}
                    accentVariant={tentativeCount > 0 ? 'warning' : 'info'}
                    className="shadow-none"
                  />
                  <CompactKpiCard
                    title="No Booking"
                    value={String(noBookingCount)}
                    icon={UserX}
                    accentVariant={noBookingCount > 0 ? 'error' : 'success'}
                    className="shadow-none"
                  />
                </div>

                {/* ── Alert banner — no-booking or over-allocated ─────────── */}
                {(noBookingCount > 0 || overAllocCount > 0) && (
                  <div className="flex items-start gap-2 rounded-md bg-warning-50 dark:bg-warning-950/20 border border-warning-200 dark:border-warning-800 px-3 py-2">
                    <AlertTriangle className="h-4 w-4 text-warning-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-warning-700 dark:text-warning-300">
                      {noBookingCount > 0 && `${noBookingCount} team member${noBookingCount !== 1 ? 's' : ''} with no booking this week`}
                      {noBookingCount > 0 && overAllocCount > 0 && ' · '}
                      {overAllocCount > 0 && `${overAllocCount} over-allocated`}
                    </p>
                  </div>
                )}

                {/* ── Team list table ─────────────────────────────────────── */}
                {totalTeamCount === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                    <Users className="h-8 w-8 opacity-25" />
                    <p className="text-sm">No team bookings found for this week</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Project</TableHead>
                          <TableHead className="text-center">Booking</TableHead>
                          <TableHead className="text-right">Alloc Hrs</TableHead>
                          <TableHead className="text-right">Actual Hrs</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleRows.map((row) => (
                          <TableRow
                            key={row.key}
                            className={cn('cursor-pointer', row.bookingStatus === 'no_booking' ? 'bg-destructive/5' : '')}
                            onClick={() => handleNavigate(`projects/${row.projectId}?tab=team`)}
                          >
                            <TableCell>
                              <span className="font-medium text-sm leading-tight whitespace-nowrap">{row.employeeName}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">{row.role}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-xs text-foreground whitespace-nowrap">{row.projectName}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <BookingStatusBadge status={row.bookingStatus} />
                            </TableCell>
                            <TableCell className="text-right">
                              {row.allocatedHours > 0 ? (
                                <span className="text-xs font-medium tabular-nums">{formatHours(row.allocatedHours)}h</span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {row.actualHours > 0 ? (
                                <span className="text-xs tabular-nums">{formatHours(row.actualHours)}h</span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <StatusBadge status={row.derivedStatus} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* ── Footer ─────────────────────────────────────────────── */}
                <div className="flex items-center justify-between pt-1 border-t">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{totalTeamCount} team member{totalTeamCount !== 1 ? 's' : ''}</span>
                    {overAllocCount > 0 && (
                      <span className="text-destructive">{overAllocCount} over-allocated</span>
                    )}
                    {atRiskCount > 0 && (
                      <span className="text-warning-600">{atRiskCount} at risk</span>
                    )}
                  </div>
                  {hasMore ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                      onClick={() => handleNavigate('resources')}
                    >
                      View full team ({teamRows.length - MAX_ROWS} more) <ArrowRight className="h-3 w-3" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                      onClick={() => handleNavigate('resources')}
                    >
                      View team <ArrowRight className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        }

        // ── Basic mode — no planner ────────────────────────────────────────────
        // Summary strip: Total Team | Over Allocated | At Risk
        // Derive Over Allocated from allocationPercent > 100%, At Risk from > 90%.
        {
          // Build basic team rows — use all allocations overlapping this week
          const basicTeamRows: TeamRow[] = pmActiveProjects.flatMap(project => {
            const projectAllocations = getProjectAllocations(project.id).filter(a =>
              overlapsThisWeek(a.startDate, a.endDate),
            );
            return projectAllocations.map(alloc => {
              const emp = findEmployee(alloc.employeeId);
              const allocPct = alloc.allocationPercent ?? 0;
              const allocHrs = alloc.allocatedHours ?? Math.round((allocPct / 100) * 40);
              const derivedStatus: 'On Track' | 'At Risk' | 'Over Allocated' =
                allocPct > 100 ? 'Over Allocated'
                : allocPct >= 90 ? 'At Risk'
                : 'On Track';
              return {
                key: `${project.id}-${alloc.employeeId}`,
                employeeId: alloc.employeeId,
                employeeName: emp?.name ?? `Employee ${alloc.employeeId}`,
                role: emp?.role ?? alloc.role ?? 'Unknown',
                projectId: project.id,
                projectName: project.name,
                bookingStatus: (alloc.status === 'tentative' ? 'tentative' : 'confirmed') as TeamRow['bookingStatus'],
                allocatedHours: allocHrs,
                actualHours: 0,
                derivedStatus,
              };
            });
          });

          basicTeamRows.sort((a, b) => {
            const bookingDiff = bookingOrder[a.bookingStatus] - bookingOrder[b.bookingStatus];
            if (bookingDiff !== 0) return bookingDiff;
            const statusDiff = statusOrder[a.derivedStatus] - statusOrder[b.derivedStatus];
            if (statusDiff !== 0) return statusDiff;
            return a.employeeName.localeCompare(b.employeeName);
          });

          const basicTotalTeam      = basicTeamRows.length;
          const basicOverAllocCount = basicTeamRows.filter(r => r.derivedStatus === 'Over Allocated').length;
          const basicAtRiskCount    = basicTeamRows.filter(r => r.derivedStatus === 'At Risk').length;

          return (
            <Card key="my_team_this_week">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">My Team This Week</CardTitle>
                  </div>
                  <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                    {weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – {weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </Badge>
                </div>
                <CardDescription className="text-xs">
                  Based on current allocations — connect a planning tool for booking status
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-0 space-y-4">
                {/* ── Summary strip — 3 tiles ─────────────────────────────── */}
                <div className="grid grid-cols-3 gap-3">
                  <CompactKpiCard
                    title="Total Team"
                    value={String(basicTotalTeam)}
                    icon={Users}
                    accentVariant="info"
                    className="shadow-none"
                  />
                  <CompactKpiCard
                    title="Over Allocated"
                    value={String(basicOverAllocCount)}
                    icon={UserX}
                    accentVariant={basicOverAllocCount > 0 ? 'error' : 'success'}
                    className="shadow-none"
                  />
                  <CompactKpiCard
                    title="At Risk"
                    value={String(basicAtRiskCount)}
                    icon={UserMinus}
                    accentVariant={basicAtRiskCount > 0 ? 'warning' : 'success'}
                    className="shadow-none"
                  />
                </div>

                {/* ── Connect planning tool prompt ─────────────────────────── */}
                <div className="flex items-start gap-2 rounded-md bg-muted/40 border px-3 py-2">
                  <WifiOff className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    Connect a planning tool to see booking status, tentative allocations, and no-booking gaps.{' '}
                    <button
                      className="underline underline-offset-2 hover:text-foreground transition-colors"
                      onClick={() => handleNavigate('settings/integrations')}
                    >
                      Connect now
                    </button>
                  </p>
                </div>

                {/* ── Attention cards — Over Allocated + At Risk only ─────── */}
                {basicTotalTeam === 0 ? null : (() => {
                  const attentionRows = basicTeamRows.filter(r => r.derivedStatus !== 'On Track');
                  const MAX_CARDS = 4;
                  const visibleAttention = attentionRows.slice(0, MAX_CARDS);
                  const overflowCount = attentionRows.length - MAX_CARDS;

                  if (attentionRows.length === 0) {
                    return (
                      <div className="flex items-center gap-3 rounded-lg bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-4 py-3">
                        <CheckCircle2 className="h-5 w-5 text-success-600 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-success-700 dark:text-success-300">All team members on track</p>
                          <p className="text-xs text-success-600 dark:text-success-400">
                            {basicTotalTeam} member{basicTotalTeam !== 1 ? 's' : ''} allocated this week, none over-allocated or at risk.
                          </p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        {visibleAttention.map(row => (
                          <button
                            key={row.key}
                            className="text-left rounded-lg border bg-muted/30 hover:bg-muted/60 transition-colors px-3 py-2.5 space-y-1"
                            onClick={() => handleNavigate(`projects/${row.projectId}?tab=team`)}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium truncate">{row.employeeName}</span>
                              <StatusBadge status={row.derivedStatus} />
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{row.role} · {row.projectName}</p>
                            {row.allocatedHours > 0 && (
                              <p className="text-xs text-muted-foreground">{formatHours(row.allocatedHours)}h allocated</p>
                            )}
                          </button>
                        ))}
                      </div>
                      {overflowCount > 0 && (
                        <button
                          className="w-full text-center text-xs text-muted-foreground hover:text-foreground py-1.5 transition-colors"
                          onClick={() => handleNavigate('resources')}
                        >
                          +{overflowCount} more team member{overflowCount !== 1 ? 's' : ''} need attention →
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* ── Footer ─────────────────────────────────────────────── */}
                <div className="flex items-center justify-between pt-1 border-t">
                  <span className="text-xs text-muted-foreground">
                    {basicTotalTeam === 0 ? 'No allocations this week' : `${basicTotalTeam} allocation${basicTotalTeam !== 1 ? 's' : ''} this week`}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                    onClick={() => handleNavigate('resources')}
                  >
                    View team <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        }
      }

      // ── Upcoming Billing Events — F26 ───────────────────────────────────────
      // PRD §4, Insight 6
      //
      // Chronological action list of billing events for the PM's active projects.
      // Default lookahead: 30 days. Events beyond 30 days are collapsed behind
      // a "Show X future events" toggle.
      //
      // Three event types:
      //   1. Date Milestone     — BillingMilestone with a dueDate, trigger = 'date'
      //   2. % Complete Milestone — trigger = 'percent-complete'; fires at completionPercent
      //   3. T&M Billing Cycle  — synthetic monthly billing event for T&M projects
      //
      // Status priority sort: Overdue → Due → Approaching → Pending → Future (collapsed)
      // T&M amounts are always labelled "Est." per PRD §2.5.
      //
      // Data: In live mode, milestones are fetched from the backend API via
      // fetchMilestonesByProjectId. In demo/tour, reads from budget mock data.
      // T&M cycle dates derived from project dates.
      case 'upcoming_billing_events': {

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const in30Days = new Date(today);
        in30Days.setDate(today.getDate() + 30);
        const in7Days = new Date(today);
        in7Days.setDate(today.getDate() + 7);
        const in60Days = new Date(today);
        in60Days.setDate(today.getDate() + 60);

        // ── Status derivation helpers ────────────────────────────────────────
        type BillingEventStatus = 'overdue' | 'due' | 'approaching' | 'pending' | 'future' | 'invoiced' | 'paid';

        function deriveDateStatus(dueDateStr: string | null | undefined): BillingEventStatus {
          if (!dueDateStr) return 'pending';
          const d = new Date(dueDateStr);
          d.setHours(0, 0, 0, 0);
          if (d < today) return 'overdue';
          if (d <= in7Days) return 'due';
          if (d <= in30Days) return 'approaching';
          return 'future';
        }

        function derivePercentStatus(
          triggerPct: number | undefined,
          completionPct: number,
          dueDateStr: string | null | undefined,
        ): BillingEventStatus {
          const threshold = triggerPct ?? 0;
          const thresholdMet = completionPct >= threshold;
          const overdeadline = dueDateStr ? new Date(dueDateStr) < today : false;
          if (thresholdMet || overdeadline) {
            if (overdeadline) return 'overdue';
            return 'due';
          }
          // Within 10% of trigger → approaching
          if (completionPct >= threshold - 10) return 'approaching';
          return 'pending';
        }

        // ── Sort order ───────────────────────────────────────────────────────
        const STATUS_ORDER: Record<BillingEventStatus, number> = {
          overdue: 0, due: 1, approaching: 2, pending: 3, future: 4, invoiced: 5, paid: 6,
        };

        // ── Row type ─────────────────────────────────────────────────────────
        interface BillingEventRow {
          key: string;
          projectId: number;
          projectName: string;
          clientName: string;
          eventType: 'Date Milestone' | '% Complete Milestone' | 'T&M Billing Cycle';
          dueDate: string | null;           // ISO date or null for pure % complete
          amount: number;
          isEstimated: boolean;             // T&M — show "Est." badge
          status: BillingEventStatus;
          triggerPercent?: number;          // % complete milestones only
          completionPercent?: number;       // project completionPercent for % context
          sortKey: number;                  // for date-based secondary sort
        }

        // ── Build event rows ─────────────────────────────────────────────────
        const allEvents: BillingEventRow[] = [];

        if (isLiveMode) {
          // Live mode: use pre-aggregated billing events from SQL endpoint
          for (const evt of liveBillingEvents) {
            const status: BillingEventStatus =
              evt.eventType === 'overdue' ? 'overdue'
              : evt.eventType === 'invoiced' ? 'invoiced'
              : deriveDateStatus(evt.dueDate);
            allEvents.push({
              key: `sql-${evt.id}`,
              projectId: evt.projectId,
              projectName: evt.projectName,
              clientName: evt.clientName ?? '',
              eventType: 'Date Milestone',
              dueDate: evt.dueDate,
              amount: evt.amount,
              isEstimated: false,
              status,
              sortKey: evt.dueDate ? new Date(evt.dueDate).getTime() : 0,
            });
          }
        } else {
          // Demo/tour mode: build from mock data per project
          for (const project of pmActiveProjects) {
            const projType = project.type;
            const completionPct = project.completionPercent ?? 0;

            if (projType === 'fixed-price') {
              const milestones: import('@/types/budget.types').BillingMilestone[] = getProjectMilestones(project.id);

              for (const m of milestones) {
                if (m.status === 'paid' || m.status === 'invoiced') {
                  allEvents.push({
                    key: `fp-${project.id}-m${m.id}-done`,
                    projectId: project.id,
                    projectName: project.name,
                    clientName: project.clientNames ?? '',
                    eventType: 'Date Milestone',
                    dueDate: m.dueDate,
                    amount: m.amount,
                    isEstimated: false,
                    status: m.status as BillingEventStatus,
                    sortKey: m.dueDate ? new Date(m.dueDate).getTime() : 0,
                  });
                  continue;
                }

                const projectTriggerType = project.billingTriggerType ?? 'date';
                const effectiveTrigger: 'date' | 'percent-complete' =
                  projectTriggerType === 'percent-complete' && m.triggerPercent != null
                    ? 'percent-complete'
                    : 'date';

                if (effectiveTrigger === 'percent-complete') {
                  const eventStatus = derivePercentStatus(m.triggerPercent, completionPct, m.dueDate);
                  allEvents.push({
                    key: `fp-${project.id}-m${m.id}-pct`,
                    projectId: project.id,
                    projectName: project.name,
                    clientName: project.clientNames ?? '',
                    eventType: '% Complete Milestone',
                    dueDate: m.dueDate ?? null,
                    amount: m.amount,
                    isEstimated: false,
                    status: eventStatus,
                    triggerPercent: m.triggerPercent,
                    completionPercent: completionPct,
                    sortKey: m.triggerPercent ?? 0,
                  });
                } else {
                  const eventStatus: BillingEventStatus =
                    m.status === 'overdue' ? 'overdue'
                    : m.status === 'due' ? 'due'
                    : deriveDateStatus(m.dueDate);
                  allEvents.push({
                    key: `fp-${project.id}-m${m.id}-date`,
                    projectId: project.id,
                    projectName: project.name,
                    clientName: project.clientNames ?? '',
                    eventType: 'Date Milestone',
                    dueDate: m.dueDate,
                    amount: m.amount,
                    isEstimated: false,
                    status: eventStatus,
                    sortKey: m.dueDate ? new Date(m.dueDate).getTime() : 0,
                  });
                }
              }

              if (milestones.length === 0) {
                const wip = wipReports.find(w => w.projectId === project.id);
                if (wip?.nextBillingEvent) {
                  const eventStatus = deriveDateStatus(wip.nextBillingEvent);
                  allEvents.push({
                    key: `fp-${project.id}-wip`,
                    projectId: project.id,
                    projectName: project.name,
                    clientName: project.clientNames ?? '',
                    eventType: 'Date Milestone',
                    dueDate: wip.nextBillingEvent,
                    amount: wip.nextBillingEventAmount ?? 0,
                    isEstimated: false,
                    status: eventStatus,
                    sortKey: new Date(wip.nextBillingEvent).getTime(),
                  });
                }
              }
            } else if (projType === 'time-and-materials') {
              const wip = wipReports.find(w => w.projectId === project.id);
              const estimatedAmount = wip ? Math.round((wip.costsIncurred / 12) * 1000) / 1000 : 0;

              const cycleDates: Date[] = [];
              const cycleStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
              for (let i = 0; i < 2; i++) {
                const cd = new Date(cycleStart);
                cd.setMonth(cd.getMonth() + i);
                if (cd <= in60Days) cycleDates.push(cd);
              }

              for (const cycleDate of cycleDates) {
                const isoDate = cycleDate.toISOString().split('T')[0];
                const eventStatus = deriveDateStatus(isoDate);
                allEvents.push({
                  key: `tm-${project.id}-${isoDate}`,
                  projectId: project.id,
                  projectName: project.name,
                  clientName: project.clientNames ?? '',
                  eventType: 'T&M Billing Cycle',
                  dueDate: isoDate,
                  amount: estimatedAmount,
                  isEstimated: true,
                  status: eventStatus,
                  sortKey: cycleDate.getTime(),
                });
              }
            } else if (projType === 'retainer') {
              const retainerDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
              const isoDate = retainerDate.toISOString().split('T')[0];
              const monthlyFee = (project.budgetRevenue ?? 0) / 12;
              allEvents.push({
                key: `retainer-${project.id}-${isoDate}`,
                projectId: project.id,
                projectName: project.name,
                clientName: project.clientNames ?? '',
                eventType: 'T&M Billing Cycle',
                dueDate: isoDate,
                amount: monthlyFee,
                isEstimated: false,
                status: deriveDateStatus(isoDate),
                sortKey: retainerDate.getTime(),
              });
            }
          }
        }

        // ── Sort: Overdue (oldest first) → Due → Approaching → Pending → Future ──
        allEvents.sort((a, b) => {
          const statusDiff = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
          if (statusDiff !== 0) return statusDiff;
          // Secondary: oldest due date first (overdue), nearest first (others)
          if (a.status === 'overdue') return a.sortKey - b.sortKey;
          return a.sortKey - b.sortKey;
        });

        // ── Partition into within-30-days and future ─────────────────────────
        const inWindowEvents = allEvents.filter(e =>
          e.status !== 'future' && e.status !== 'paid' && e.status !== 'invoiced'
        );
        const futureEvents = allEvents.filter(e => e.status === 'future');
        const completedEvents = allEvents.filter(e => e.status === 'paid' || e.status === 'invoiced');

        const MAX_ROWS = 8;
        const visibleInWindow = inWindowEvents.slice(0, MAX_ROWS);
        const hasMoreInWindow = inWindowEvents.length > MAX_ROWS;

        // ── Format helpers ───────────────────────────────────────────────────
        function fmtCurrency(v: number) {
          return new Intl.NumberFormat('en-GB', {
            style: 'currency', currency: 'GBP', maximumFractionDigits: 0,
          }).format(v);
        }

        function fmtDate(iso: string | null | undefined) {
          if (!iso) return '—';
          return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        }

        // ── Event type badge ─────────────────────────────────────────────────
        function EventTypeBadge({ type }: { type: BillingEventRow['eventType'] }) {
          if (type === 'Date Milestone') {
            return (
              <Badge variant="outline" className="text-xs border-blue-200 text-blue-700 bg-blue-50 gap-1 whitespace-nowrap">
                <Calendar className="h-3 w-3" />
                Date Milestone
              </Badge>
            );
          }
          if (type === '% Complete Milestone') {
            return (
              <Badge variant="outline" className="text-xs border-purple-200 text-purple-700 bg-purple-50 gap-1 whitespace-nowrap">
                <Percent className="h-3 w-3" />
                % Complete
              </Badge>
            );
          }
          return (
            <Badge variant="outline" className="text-xs border-teal-200 text-teal-700 bg-teal-50 gap-1 whitespace-nowrap">
              <RefreshCw className="h-3 w-3" />
              T&amp;M Cycle
            </Badge>
          );
        }

        // ── Status badge ─────────────────────────────────────────────────────
        function StatusBadge({ status }: { status: BillingEventStatus }) {
          const map: Record<BillingEventStatus, { label: string; className: string }> = {
            overdue:    { label: 'Overdue',    className: 'bg-destructive/10 text-destructive border-destructive/20' },
            due:        { label: 'Due',        className: 'bg-warning-100 text-warning-700 border-warning-200' },
            approaching:{ label: 'Approaching',className: 'bg-warning-50 text-warning-600 border-warning-200' },
            pending:    { label: 'Pending',    className: 'bg-blue-50 text-blue-700 border-blue-200' },
            future:     { label: 'Future',     className: 'bg-muted/50 text-muted-foreground border-border' },
            invoiced:   { label: 'Invoiced',   className: 'bg-success-50 text-success-700 border-success-200' },
            paid:       { label: 'Paid',       className: 'bg-success-100 text-success-600 border-success-200' },
          };
          const cfg = map[status] ?? map.pending;
          return (
            <Badge variant="outline" className={cn('text-xs whitespace-nowrap', cfg.className)}>
              {cfg.label}
            </Badge>
          );
        }

        // ── Empty state ──────────────────────────────────────────────────────
        if (pmActiveProjects.length === 0) {
          return (
            <Card key="upcoming_billing_events">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">Upcoming Billing Events</CardTitle>
                </div>
                <CardDescription>No active projects</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                  <CalendarClock className="h-10 w-10 opacity-25" />
                  <p className="text-sm font-medium">No active projects</p>
                  <p className="text-xs">Billing events will appear here once you have active projects.</p>
                </div>
              </CardContent>
            </Card>
          );
        }

        // ── All up to date state ─────────────────────────────────────────────
        if (inWindowEvents.length === 0 && futureEvents.length === 0) {
          return (
            <Card key="upcoming_billing_events" className="border-success-200 dark:border-success-800">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-success-600" />
                  <CardTitle className="text-base">Upcoming Billing Events</CardTitle>
                </div>
                <CardDescription>Next 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 rounded-lg bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-4 py-3">
                  <CheckCircle2 className="h-5 w-5 text-success-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-success-700 dark:text-success-300">All billing up to date</p>
                    <p className="text-xs text-success-600 dark:text-success-400">
                      No billing events due in the next 30 days across your projects.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        }

        // ── Full widget ──────────────────────────────────────────────────────
        const overdueCount    = inWindowEvents.filter(e => e.status === 'overdue').length;
        const dueCount        = inWindowEvents.filter(e => e.status === 'due').length;
        const approachingCount = inWindowEvents.filter(e => e.status === 'approaching').length;

        return (
          <Card key="upcoming_billing_events" className="overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base">Upcoming Billing Events</CardTitle>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                    Next 30 days
                  </Badge>
                </div>
              </div>
              <CardDescription className="text-xs mt-0.5">
                Milestones, T&amp;M cycles, and % complete triggers across your projects
              </CardDescription>
            </CardHeader>

            <CardContent className="p-0 space-y-0">
              {/* ── Status summary strip ────────────────────────────────────── */}
              {(overdueCount > 0 || dueCount > 0 || approachingCount > 0) && (
                <div className="px-4 pb-3 pt-0 flex items-center gap-3 flex-wrap">
                  {overdueCount > 0 && (
                    <div className="flex items-center gap-1.5 rounded-md bg-destructive/10 border border-destructive/20 px-2.5 py-1">
                      <AlertCircle className="h-3 w-3 text-destructive shrink-0" />
                      <span className="text-xs font-medium text-destructive">
                        {overdueCount} overdue
                      </span>
                    </div>
                  )}
                  {dueCount > 0 && (
                    <div className="flex items-center gap-1.5 rounded-md bg-warning-50 border border-warning-200 px-2.5 py-1">
                      <AlertTriangle className="h-3 w-3 text-warning-600 shrink-0" />
                      <span className="text-xs font-medium text-warning-700">
                        {dueCount} due soon
                      </span>
                    </div>
                  )}
                  {approachingCount > 0 && (
                    <div className="flex items-center gap-1.5 rounded-md bg-blue-50 border border-blue-200 px-2.5 py-1">
                      <CalendarDays className="h-3 w-3 text-blue-600 shrink-0" />
                      <span className="text-xs font-medium text-blue-700">
                        {approachingCount} approaching
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* ── Event table ─────────────────────────────────────────────── */}
              {visibleInWindow.length > 0 && (
                <div className="w-full">
                  <Table>
                    <TableHeader divided>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>Project / Client</TableHead>
                        <TableHead>Event Type</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Trigger</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleInWindow.map((event) => (
                        <TableRow
                          key={event.key}
                          className={cn('cursor-pointer', event.status === 'overdue' ? 'bg-destructive/5' : '')}
                          onClick={() => handleNavigate(`projects/${event.projectId}?tab=billing`)}
                        >
                          <TableCell>
                            <div className="font-medium text-foreground text-xs leading-snug">{event.projectName}</div>
                            {event.clientName && (
                              <div className="text-[10px] text-muted-foreground mt-0.5">{event.clientName}</div>
                            )}
                          </TableCell>

                          <TableCell>
                            <EventTypeBadge type={event.eventType} />
                          </TableCell>

                          <TableCell>
                            {event.dueDate ? (
                              <span className={cn(
                                'text-xs whitespace-nowrap',
                                event.status === 'overdue' ? 'text-destructive font-medium' :
                                event.status === 'due' ? 'text-warning-700 font-medium' :
                                'text-foreground',
                              )}>
                                {fmtDate(event.dueDate)}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">At threshold</span>
                            )}
                          </TableCell>

                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-xs font-medium tabular-nums">
                                {event.amount > 0 ? fmtCurrency(event.amount) : '—'}
                              </span>
                              {event.isEstimated && event.amount > 0 && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 border-muted-foreground/30 text-muted-foreground">
                                  Est.
                                </Badge>
                              )}
                            </div>
                          </TableCell>

                          <TableCell>
                            {event.eventType === '% Complete Milestone' && event.triggerPercent != null ? (
                              <div>
                                <span className="text-xs text-foreground">At {event.triggerPercent}%</span>
                                {event.completionPercent != null && (
                                  <div className={cn(
                                    'text-[10px] mt-0.5',
                                    (event.completionPercent ?? 0) >= event.triggerPercent ? 'text-success-600' :
                                    (event.completionPercent ?? 0) >= event.triggerPercent - 10 ? 'text-warning-600' :
                                    'text-muted-foreground',
                                  )}>
                                    currently {event.completionPercent}%
                                  </div>
                                )}
                              </div>
                            ) : event.eventType === 'T&M Billing Cycle' ? (
                              <span className="text-xs text-muted-foreground">Monthly cycle</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">Date trigger</span>
                            )}
                          </TableCell>

                          <TableCell>
                            <StatusBadge status={event.status} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* ── "View all" if more than MAX_ROWS in window ───────────────── */}
              {hasMoreInWindow && (
                <div className="px-4 py-2 border-b border-border/50">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1 text-muted-foreground"
                    onClick={() => handleNavigate('projects')}
                  >
                    View all {inWindowEvents.length} billing events <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              )}

              {/* ── Future events (collapsed, >30 days) ─────────────────────── */}
              {futureEvents.length > 0 && (
                <div className="border-t border-border/50">
                  <button
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors"
                    onClick={() => setShowFutureEvents(prev => !prev)}
                  >
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Calendar className="h-3 w-3" />
                      {futureEvents.length} future event{futureEvents.length !== 1 ? 's' : ''} (&gt;30 days)
                    </span>
                    {showFutureEvents
                      ? <ChevronsUp className="h-3.5 w-3.5 text-muted-foreground" />
                      : <ChevronsDown className="h-3.5 w-3.5 text-muted-foreground" />
                    }
                  </button>

                  {showFutureEvents && (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableBody>
                          {futureEvents.map((event) => (
                            <TableRow
                              key={event.key}
                              className="cursor-pointer bg-muted/5"
                              onClick={() => handleNavigate(`projects/${event.projectId}?tab=billing`)}
                            >
                              <TableCell>
                                <div className="text-xs font-medium text-muted-foreground">{event.projectName}</div>
                                {event.clientName && (
                                  <div className="text-[10px] text-muted-foreground/70">{event.clientName}</div>
                                )}
                              </TableCell>
                              <TableCell>
                                <EventTypeBadge type={event.eventType} />
                              </TableCell>
                              <TableCell>
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {fmtDate(event.dueDate)}
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <span className="text-xs text-muted-foreground tabular-nums">
                                    {event.amount > 0 ? fmtCurrency(event.amount) : '—'}
                                  </span>
                                  {event.isEstimated && event.amount > 0 && (
                                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 border-muted-foreground/30 text-muted-foreground">
                                      Est.
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {event.eventType === '% Complete Milestone' && event.triggerPercent != null ? (
                                  <span className="text-xs text-muted-foreground">At {event.triggerPercent}%</span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <StatusBadge status={event.status} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}

              {/* ── Footer ──────────────────────────────────────────────────── */}
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/10">
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{inWindowEvents.length} event{inWindowEvents.length !== 1 ? 's' : ''} in next 30 days</span>
                  {completedEvents.length > 0 && (
                    <span className="text-success-600">{completedEvents.length} invoiced/paid</span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                  onClick={() => handleNavigate('projects')}
                >
                  View all projects <ArrowRight className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      }

      // ── Timesheet Status — F27 ──────────────────────────────────────────────
      // PRD §4, Insight 7 — Mode-aware card, 3 states:
      //
      //  Full mode   (timesheetIntegration.status = 'connected'):
      //    Summary strip (Submitted / Not Submitted / Rejected tiles) +
      //    per-person table with NormalizedApprovalStatus badge.
      //    Columns: Name | Project | Hours Logged | Billable Hours | Status | Submitted At
      //    hasRejections → row pushed to top.
      //    Positive all-clear when all submitted/approved.
      //    Week navigation: previous-week arrow.
      //
      //  Basic mode  (integration configured but not connected):
      //    Derives timesheet data from TimeEntry records (local actuals).
      //    Summary strip: Name | Project | Hours | Expected Hrs | Variance.
      //    "Connect Access Time" nudge at bottom.
      //
      //  Not Configured state:
      //    Clean value-proposition card with "Connect timesheet system" CTA.
      //
      //  Data note: Team data is sourced from fetchResourceAllocations + fetchEmployees
      //  in live mode, falling back to mock data in demo/tour. Timesheet entries are
      //  still simulated client-side (no dedicated timesheet API endpoint yet).
      //  The backend gap-filling (people with no entry shown as 'no_entry') is replicated
      //  client-side here: cross-reference allocated team against entries.
      case 'timesheet_status': {
        // ── Hours & Cost Actuals ──────────────────────────────────────────────
        // Sourced from project financials (actualHours, budgetHours, actualCosts,
        // budgetCost, billableHours) — no external time integration required.

        if (projectsLoading) {
          return (
            <Card key="timesheet_status">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">Hours &amp; Cost Actuals</CardTitle>
                </div>
                <CardDescription>Actual vs budget across your active projects</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-14 w-full rounded-md" />
                ))}
              </CardContent>
            </Card>
          );
        }

        if (pmActiveProjects.length === 0) {
          return (
            <Card key="timesheet_status">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">Hours &amp; Cost Actuals</CardTitle>
                </div>
                <CardDescription>Actual vs budget across your active projects</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                  <FolderKanban className="h-8 w-8 opacity-30" />
                  <p className="text-sm">No active projects</p>
                </div>
              </CardContent>
            </Card>
          );
        }

        // ── Derive per-project actuals rows ───────────────────────────────────
        interface ActualsRow {
          id: number;
          name: string;
          actualHours: number;
          budgetHours: number;
          hoursBurnPct: number;       // 0–100+
          billableHours: number;
          billablePct: number;        // billable / actual
          actualCost: number;
          budgetCost: number;
          costBurnPct: number;        // 0–100+
          riskLevel: 'over' | 'at_risk' | 'on_track';
        }

        const actualsRows: ActualsRow[] = pmActiveProjects.map(p => {
          const actualHours  = p.actualHours  ?? 0;
          const budgetHours  = p.budgetHours  ?? 0;
          const billableHrs  = p.billableHours ?? 0;
          const actualCost   = p.actualCosts   ?? 0;
          const budgetCost   = p.budgetCost ?? ((p.budgetLabour ?? 0) + (p.budgetExpense ?? 0));

          const hoursBurnPct = budgetHours > 0 ? Math.round((actualHours / budgetHours) * 100) : 0;
          const costBurnPct  = budgetCost  > 0 ? Math.round((actualCost  / budgetCost)  * 100) : 0;
          const billablePct  = actualHours > 0 ? Math.round((billableHrs / actualHours) * 100) : 0;

          const maxBurn = Math.max(hoursBurnPct, costBurnPct);
          const riskLevel: ActualsRow['riskLevel'] =
            maxBurn >= 100 ? 'over' :
            maxBurn >= 80  ? 'at_risk' :
            'on_track';

          return {
            id: p.id,
            name: p.name,
            actualHours,
            budgetHours,
            hoursBurnPct,
            billableHours: billableHrs,
            billablePct,
            actualCost,
            budgetCost,
            costBurnPct,
            riskLevel,
          };
        });

        // Sort: over → at_risk → on_track, then by burn % desc
        const riskOrder: Record<ActualsRow['riskLevel'], number> = { over: 0, at_risk: 1, on_track: 2 };
        const sortedActuals = [...actualsRows].sort(
          (a, b) => riskOrder[a.riskLevel] - riskOrder[b.riskLevel]
            || b.hoursBurnPct - a.hoursBurnPct,
        );

        // ── Summary totals ────────────────────────────────────────────────────
        const totalActualHours   = actualsRows.reduce((s, r) => s + r.actualHours,   0);
        const totalBudgetHours   = actualsRows.reduce((s, r) => s + r.budgetHours,   0);
        const totalBillableHours = actualsRows.reduce((s, r) => s + r.billableHours, 0);
        const totalActualCost    = actualsRows.reduce((s, r) => s + r.actualCost,    0);
        const totalBudgetCost    = actualsRows.reduce((s, r) => s + r.budgetCost,    0);
        const overallHoursBurn   = totalBudgetHours > 0 ? Math.round((totalActualHours / totalBudgetHours) * 100) : 0;
        const overallCostBurn    = totalBudgetCost  > 0 ? Math.round((totalActualCost  / totalBudgetCost)  * 100) : 0;
        const overallBillablePct = totalActualHours > 0 ? Math.round((totalBillableHours / totalActualHours) * 100) : 0;

        const overCount    = actualsRows.filter(r => r.riskLevel === 'over').length;
        const atRiskCount  = actualsRows.filter(r => r.riskLevel === 'at_risk').length;

        // ── Risk badge helper ─────────────────────────────────────────────────
        function RiskBadge({ level }: { level: ActualsRow['riskLevel'] }) {
          const cfg = {
            over:     { label: 'Over Budget',  className: 'bg-destructive/10 text-destructive border-destructive/20' },
            at_risk:  { label: 'At Risk',       className: 'bg-warning-100 text-warning-700 border-warning-200' },
            on_track: { label: 'On Track',      className: 'bg-success-100 text-success-700 border-success-200' },
          };
          const c = cfg[level];
          return (
            <Badge variant="outline" className={cn('text-xs font-medium whitespace-nowrap', c.className)}>
              {c.label}
            </Badge>
          );
        }

        // ── Burn bar colour ───────────────────────────────────────────────────
        function burnBarClass(pct: number) {
          if (pct >= 100) return 'bg-destructive';
          if (pct >= 80)  return 'bg-warning-500';
          return 'bg-success-500';
        }

        const fmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

        return (
          <Card key="timesheet_status">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">Hours &amp; Cost Actuals</CardTitle>
                </div>
                <div className="flex items-center gap-1.5">
                  {overCount > 0 && (
                    <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/20">
                      {overCount} over budget
                    </Badge>
                  )}
                  {atRiskCount > 0 && (
                    <Badge variant="outline" className="text-xs bg-warning-100 text-warning-700 border-warning-200">
                      {atRiskCount} at risk
                    </Badge>
                  )}
                </div>
              </div>
              <CardDescription>Actual hours and cost burn vs budget across your active projects</CardDescription>
            </CardHeader>

            <CardContent className="p-0">
              {/* Summary strip */}
              <div className="grid grid-cols-3 gap-3 border-b border-border p-3">
                <CompactKpiCard
                  title="Hours Burn"
                  value={`${overallHoursBurn}%`}
                  icon={Clock}
                  description={`${formatHours(totalActualHours)}h of ${formatHours(totalBudgetHours)}h`}
                  accentVariant={
                    overallHoursBurn >= 100 ? 'error' :
                    overallHoursBurn >= 80  ? 'warning' :
                    'success'
                  }
                  className="shadow-none"
                />
                <CompactKpiCard
                  title="Cost Burn"
                  value={`${overallCostBurn}%`}
                  icon={DollarSign}
                  description={`${fmt.format(totalActualCost)} of ${fmt.format(totalBudgetCost)}`}
                  accentVariant={
                    overallCostBurn >= 100 ? 'error' :
                    overallCostBurn >= 80  ? 'warning' :
                    'success'
                  }
                  className="shadow-none"
                />
                <CompactKpiCard
                  title="Billability"
                  value={`${overallBillablePct}%`}
                  icon={Percent}
                  description={`${formatHours(totalBillableHours)}h billable`}
                  accentVariant={
                    overallBillablePct >= 70 ? 'success' : 'warning'
                  }
                  className="shadow-none"
                />
              </div>

              {/* Per-project table — top 5 by risk */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader divided>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Project</TableHead>
                      <TableHead>Hours Burn</TableHead>
                      <TableHead className="text-right">Actual Hrs</TableHead>
                      <TableHead className="text-right">Billable %</TableHead>
                      <TableHead className="text-right">Cost Burn</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedActuals.slice(0, 5).map(row => (
                      <TableRow
                        key={row.id}
                        className={cn(
                          'cursor-pointer hover:bg-muted/40',
                          row.riskLevel === 'over' && 'bg-destructive/5',
                        )}
                        onClick={() => handleNavigate(`projects/${row.id}?tab=cost-budget`)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-1.5 min-w-[140px]">
                            {row.riskLevel === 'over' && (
                              <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                            )}
                            {row.riskLevel === 'at_risk' && (
                              <AlertTriangle className="h-3.5 w-3.5 text-warning-500 shrink-0" />
                            )}
                            <span className="text-xs font-medium truncate max-w-[180px]">{row.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="w-[140px]">
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                              <span>{formatHours(row.actualHours)}h / {formatHours(row.budgetHours)}h</span>
                              <span className={cn(
                                'font-medium',
                                row.hoursBurnPct >= 100 ? 'text-destructive' :
                                row.hoursBurnPct >= 80  ? 'text-warning-600' :
                                'text-muted-foreground',
                              )}>
                                {row.hoursBurnPct}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                              <div
                                className={cn('h-full rounded-full transition-all', burnBarClass(row.hoursBurnPct))}
                                style={{ width: `${Math.min(row.hoursBurnPct, 100)}%` }}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                          {formatHours(row.actualHours)}h
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={cn(
                            'text-xs font-medium tabular-nums',
                            row.billablePct < 70 ? 'text-warning-600' : 'text-muted-foreground',
                          )}>
                            {row.billablePct}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                          <span className={cn(
                            row.costBurnPct >= 100 ? 'text-destructive font-medium' :
                            row.costBurnPct >= 80  ? 'text-warning-600 font-medium' :
                            'text-muted-foreground',
                          )}>
                            {row.costBurnPct}%
                          </span>
                        </TableCell>
                        <TableCell>
                          <RiskBadge level={row.riskLevel} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20 text-xs text-muted-foreground">
                <span>
                  Showing {Math.min(5, sortedActuals.length)} of {pmActiveProjects.length} active project{pmActiveProjects.length !== 1 ? 's' : ''}
                  {sortedActuals.length > 5 && (
                    <span className="ml-1 text-muted-foreground/60">
                      — sorted by risk
                    </span>
                  )}
                </span>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => handleNavigate('projects')}
                >
                  {sortedActuals.length > 5 ? `View all ${pmActiveProjects.length} projects` : 'View all projects'}
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      }

      // ── Quick Links — PM (F28) ──────────────────────────────────────────────
      case 'quick_links_pm': {
        // Build managerId query param for pre-filtered report links.
        // currentPmId is already derived above from authenticatedUser.
        const pmParam = currentPmId ? `?managerId=${currentPmId}` : '';

        const pmQuickLinks: {
          id: string;
          label: string;
          description: string;
          route: string;
          icon: React.ReactNode;
        }[] = [
          {
            id: 'my-projects',
            label: 'My Projects',
            description: 'View all projects assigned to you with health and status',
            route: `projects${pmParam}`,
            icon: <FolderKanban className="h-5 w-5" />,
          },
          {
            id: 'project-status-report',
            label: 'Project Status Report',
            description: 'Full status snapshot across all your active projects',
            route: `reports/project-status${pmParam}`,
            icon: <ClipboardList className="h-5 w-5" />,
          },
          {
            id: 'budget-variance-report',
            label: 'Budget Variance',
            description: 'Cost and revenue variance vs budget for your projects',
            route: `reports/budget-variance${pmParam}`,
            icon: <TrendingUp className="h-5 w-5" />,
          },
          {
            id: 'project-pl',
            label: 'Project P&L',
            description: 'Project-level profit and loss with actuals vs budget',
            route: `reports/project-pl${pmParam}`,
            icon: <FileText className="h-5 w-5" />,
          },
          {
            id: 'wip-report',
            label: 'WIP Report',
            description: 'Work in progress balances and aging for your projects',
            route: `reports/wip${pmParam}`,
            icon: <BookOpen className="h-5 w-5" />,
          },
          {
            id: 'project-forecast',
            label: 'Project Forecast',
            description: 'Forward-looking cost and revenue forecast per project',
            route: `reports/project-forecast${pmParam}`,
            icon: <BarChart3 className="h-5 w-5" />,
          },
        ];

        return (
          <Card key="quick_links_pm" className="col-span-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Quick Links</CardTitle>
              <CardDescription>Shortcuts to your most-used reports and views</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 items-stretch">
                {pmQuickLinks.map(({ id, label, description, route, icon }) => (
                  <button
                    key={id}
                    onClick={() => handleNavigate(route)}
                    className="group relative flex flex-col items-start gap-2 rounded-lg border border-border bg-card p-4 text-left transition-colors cursor-pointer hover:border-primary/50 hover:bg-accent/50 h-full justify-between"
                  >
                    {/* Top: icon + label + description */}
                    <div className="flex flex-col items-start gap-2 w-full">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary group-hover:bg-primary/20">
                        {icon}
                      </span>
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium leading-tight text-foreground">
                          {label}
                        </p>
                        <p className="text-xs text-muted-foreground leading-snug line-clamp-2">
                          {description}
                        </p>
                      </div>
                    </div>

                    {/* Bottom: arrow always pinned */}
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary mt-1" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      }

      // ── Cross-persona additions rendered in PM context ──────────────────────
      // These widgets live in other personas' registries but can be added by PM
      // users via the settings cog (curated additions — PRD §2.2).
      case 'billing_pipeline': {
        // PM-scoped billing pipeline — only projects this PM manages, scoped
        // server-side in live mode.
        const pmReadyToRaise    = wipView.readyToBill;
        const pmReadyAmount     = wipView.readyToBillAmount;
        const pmComingUpAmount  = wipView.upcomingAmount;
        const pmOverdue         = wipView.overdue;
        const pmOverdueAmount   = wipView.overdueAmount;

        const pmBillingTabs = [
          { id: 'ready' as const,   label: 'Ready to Raise',  count: wipView.readyToBillCount, amount: pmReadyAmount },
          { id: 'coming' as const,  label: 'Coming Up',       count: wipView.upcomingCount,    amount: pmComingUpAmount },
          { id: 'overdue' as const, label: 'Overdue / Chase', count: wipView.overdueCount,     amount: pmOverdueAmount },
        ];

        // Local tab state is handled via a separate key inside the PM shell
        // We use a simple display of the 'ready' queue inline (no tab state needed for PM view)
        const fmtC = (v: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);
        const fmtD = (iso: string | null | undefined) => !iso ? '—' : new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

        return (
          <Card key="billing_pipeline_pm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Banknote className="h-4 w-4 text-muted-foreground" />Billing Pipeline
                  </CardTitle>
                  <CardDescription className="mt-0.5 flex items-center gap-1.5">
                    Your projects only
                    {showFinanceWidgetScopeIndicator && <Badge variant="outline" className="text-[10px] font-normal text-sky-600 border-sky-300">Scoped</Badge>}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleNavigate('invoicing')} className="gap-1 text-xs text-muted-foreground">
                  View invoicing <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <CompactKpiCard
                  title="Ready to Raise"
                  value={String(pmBillingTabs.find(t => t.id === 'ready')!.count)}
                  icon={CheckCircle2}
                  description={pmBillingTabs.find(t => t.id === 'ready')!.amount > 0 ? fmtC(pmBillingTabs.find(t => t.id === 'ready')!.amount) : undefined}
                  accentVariant={pmBillingTabs.find(t => t.id === 'ready')!.count > 0 ? 'success' : 'info'}
                  className="shadow-none"
                />
                <CompactKpiCard
                  title="Coming Up"
                  value={String(pmBillingTabs.find(t => t.id === 'coming')!.count)}
                  icon={CalendarClock}
                  description={pmBillingTabs.find(t => t.id === 'coming')!.amount > 0 ? fmtC(pmBillingTabs.find(t => t.id === 'coming')!.amount) : undefined}
                  accentVariant="info"
                  className="shadow-none"
                />
                <CompactKpiCard
                  title="Overdue / Chase"
                  value={String(pmBillingTabs.find(t => t.id === 'overdue')!.count)}
                  icon={AlertCircle}
                  description={pmBillingTabs.find(t => t.id === 'overdue')!.amount > 0 ? fmtC(pmBillingTabs.find(t => t.id === 'overdue')!.amount) : undefined}
                  accentVariant={pmBillingTabs.find(t => t.id === 'overdue')!.count > 0 ? 'error' : 'success'}
                  className="shadow-none"
                />
              </div>
              {pmReadyToRaise.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Ready to Raise</p>
                  {pmReadyToRaise.slice(0, 5).map(r => (
                    <div key={r.projectId} className="flex items-center justify-between gap-2 rounded-md border bg-success-50 border-success-200 px-3 py-2 cursor-pointer hover:bg-success-100 transition-colors" onClick={() => handleNavigate(`projects/${r.projectId}?tab=billing`)}>
                      <div className="min-w-0">
                        <div className="text-xs font-medium truncate">{r.projectName}</div>
                        <div className="text-[10px] text-muted-foreground">{r.clientName}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs font-medium text-success-600 tabular-nums">{fmtC(r.nextBillingEventAmount ?? 0)}</span>
                        <ArrowRight className="h-3 w-3 text-success-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {pmOverdue.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-destructive uppercase tracking-wide">Overdue</p>
                  {pmOverdue.slice(0, 3).map(r => (
                    <div key={r.projectId} className="flex items-center justify-between gap-2 rounded-md border bg-destructive/5 border-destructive/20 px-3 py-2 cursor-pointer hover:bg-destructive/10 transition-colors" onClick={() => handleNavigate(`projects/${r.projectId}?tab=billing`)}>
                      <div className="min-w-0">
                        <div className="text-xs font-medium truncate">{r.projectName}</div>
                        <div className="text-[10px] text-muted-foreground">Due: {fmtD(r.nextBillingEvent)}</div>
                      </div>
                      <span className="text-xs font-medium text-destructive tabular-nums shrink-0">{fmtC(r.nextBillingEventAmount ?? 0)}</span>
                    </div>
                  ))}
                </div>
              )}
              {pmReadyToRaise.length === 0 && pmOverdue.length === 0 && (
                <div className="flex items-center gap-2 rounded-md bg-success-50 border border-success-200 px-3 py-2.5 text-xs text-success-700">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />All billing up to date
                </div>
              )}
            </CardContent>
          </Card>
        );
      }
      case 'wip_aging_overview': {
        // PM-scoped WIP aging — only projects this PM manages, scoped
        // server-side in live mode.
        const pmWipCount = wipView.positiveWipCount;
        const pmWipTotal = wipView.positiveWipTotal;
        // This widget has always shown the eight oldest rows and has no band
        // filter, so the first server page (ten rows, same ageing-descending
        // order) contains exactly the eight it used to pick out of the full set.
        const pmWipReports = wipView.tableRows;
        const fmtC = (v: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);

        return (
          <Card key="wip_aging_overview_pm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />WIP Aging Overview
                  </CardTitle>
                  <CardDescription className="mt-0.5 flex items-center gap-1.5">
                    Your projects only
                    {showFinanceWidgetScopeIndicator && <Badge variant="outline" className="text-[10px] font-normal text-sky-600 border-sky-300">Scoped</Badge>}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleNavigate('wip-reporting')} className="gap-1 text-xs text-muted-foreground">
                  View WIP report <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {pmWipCount === 0 ? (
                <div className="flex items-center gap-2 rounded-md bg-success-50 border border-success-200 px-3 py-2.5 text-xs text-success-700">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />No outstanding WIP — all work is billed up to date
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Your projects total WIP:</span>
                    <span className="font-semibold text-foreground">{fmtC(pmWipTotal)}</span>
                  </div>
                  <div className="space-y-1.5">
                    {[...pmWipReports]
                      .sort((a, b) => (b.agingDays ?? 0) - (a.agingDays ?? 0))
                      .slice(0, 8)
                      .map(r => {
                        const agingDays = r.agingDays ?? 0;
                        const agingColour = agingDays > 90 ? 'text-destructive' : agingDays > 60 ? 'text-orange-600' : agingDays > 30 ? 'text-warning-600' : 'text-success-600';
                        return (
                          <div key={r.projectId} className="flex items-center justify-between gap-2 rounded-md border bg-muted/20 px-3 py-2 cursor-pointer hover:bg-muted/40 transition-colors" onClick={() => handleNavigate(`projects/${r.projectId}`)}>
                            <div className="min-w-0">
                              <div className="text-xs font-medium truncate">{r.projectName}</div>
                              <div className="text-[10px] text-muted-foreground">{r.clientName}</div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0 text-xs">
                              <span className={cn('font-medium tabular-nums', agingColour)}>{agingDays}d</span>
                              <span className="font-medium tabular-nums text-foreground">{fmtC(r.wipAmount)}</span>
                              {r.readyToBill && <Badge variant="outline" className="text-[10px] font-normal text-success-600 border-success-300">Ready</Badge>}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        );
      }
      case 'client_concentration_risk':
        return (
          <ComingSoonCard
            key="client_concentration_risk_pm"
            widgetId="F14"
            title="Client Concentration Risk"
            description="Remaining contract value distribution (from Executive dashboard)"
          />
        );
      case 'portfolio_profitability':
        return (
          <ComingSoonCard
            key="portfolio_profitability_pm"
            widgetId="F10"
            title="Portfolio Profitability"
            description="6-month margin trend + top/bottom 3 projects (from Executive dashboard)"
          />
        );

      default:
        return null;
    }
  }

  return (
    <div className="space-y-6">
      {orderedVisibleWidgets
        .filter(id => !isWidgetHiddenForReleaseAnyPersona(id))
        .map(id =>
          isPmWidgetReady(id) ? renderWidget(id) : <PmWidgetSkeleton key={id} widgetId={id} />
        )}
    </div>
  );
}
